import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useBelegProjekte } from "@/components/BelegProjektFeld"; // A-015
import BelegProjektSchild from "@/components/BelegProjektSchild"; // A-015b
import BauvorhabenStift from "@/components/BauvorhabenStift"; // A-020 P4
import { useF1Help } from "@/lib/useF1Help";
import { HelpSlideOver } from "@/components/HelpSlideOver"; // A-015: Editor liegt außerhalb des Hauptlayouts
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { Package, CheckCircle, FileText, ClipboardCheck, Receipt, Search, Star, X, Send, Save, FileSearch, FileCheck2 } from "lucide-react";
import { Button, Modal } from "@/components/common";
import { api, API } from "@/lib/api";
import { starteVorgang, beendeVorgang, aktion as ablaufAktion } from "@/lib/ablauf";
import { BausteineAnsicht, useSchmal } from "@/components/bausteine/BausteineAnsicht"; // Bausteine-Profi
import { bausteineMenue, bausteineFensterOeffnen, useBelegEmpfang, vorlagenMenue, vorlagenFensterOeffnen } from "@/lib/windowSync";
import { fuerBeleg } from "@/lib/bausteine";
import { TextTemplateSelect, getAnredeBrief, resolvePlaceholders } from "@/components/TextTemplateSelect";

// Sub-components
import { EditorToolbar } from "@/components/wysiwyg/EditorToolbar";
import { EditorSidebar } from "@/components/wysiwyg/EditorSidebar";
import { LohnkostenSidebar } from "@/components/wysiwyg/LohnkostenSidebar";
import { lohnZeilen, lohnSumme, lohnBeschreibung, rabattFaktor, positionMitHandLohn, positionAutomatisch } from "@/lib/lohnanteil";
import { lohnanteilFragen } from "@/lib/lohnFrage";
import { DocumentHeader } from "@/components/wysiwyg/DocumentHeader";
import { PositionsTable } from "@/components/wysiwyg/PositionsTable";
import { TotalsSection } from "@/components/wysiwyg/TotalsSection";
import { AnzahlungPanel } from "@/components/wysiwyg/AnzahlungPanel";
import { SettingsSlideOver } from "@/components/wysiwyg/SettingsSlideOver";
import { DocumentPreview } from "@/components/DocumentPreview";
import { TextKorrekturModal, TextKorrekturButton } from "@/components/wysiwyg/TextKorrekturModal";
import { DocumentCheckModal } from "@/components/wysiwyg/DocumentCheckModal";
import { PdfPreviewModal } from "@/components/wysiwyg/PdfPreviewModal";
import { VorlageOeffnenFenster, VorlageFrage } from "@/components/wysiwyg/VorlageOeffnenFenster"; // 01.11.2026: „Vorlage öffnen“ = Liste aus den Einstellungen
import { validateDocument } from "@/lib/documentValidator";
import { angebotFestschreiben } from "@/lib/angebotFestschreiben";
import { TestMarke } from "@/components/TestMarke";
import { frageVerlassen } from "@/lib/ungespeichertWarnung";
import { mitTestkundeRueckfrage } from "@/lib/testkundeRueckfrage";
import { anschriftTeile, anschriftBesonders } from "@/lib/anschrift";
import { ladeStandardVorlage } from "@/lib/textvorlagen";

const AUTOSAVE_ANLASS = "Automatisch speichern (60 s)";

// A-020: Kunde → Empfänger im Beleg (Name + Adresse aus den Feldern; P2b: Zeilen/Reihenfolge des Kunden über lib/anschrift)
const kundeFuerBeleg = (cust) => {
  const addr = cust.address || `${cust.strasse || ""} ${cust.hausnummer || ""}, ${cust.plz || ""} ${cust.ort || ""}`.trim().replace(/^,\s*/, "").replace(/,\s*$/, "");
  const name = (cust.vorname || cust.nachname) ? `${cust.vorname || ""} ${cust.nachname || ""}`.trim() : cust.name;
  if (anschriftBesonders(cust)) {
    const zeilen = anschriftTeile(cust).map((t) => t.text);
    return { ...cust, name: zeilen[0] || "", address: zeilen.slice(1).join("\n"), anschrift_frei: true };
  }
  return { ...cust, name, address: addr, anschrift_frei: false };
};
// A-020: Zeilen für den Server (nur nach „Anschrift anpassen“, sonst null = Server nimmt die Kundendaten samt Adresszeilen)
const anschriftZeilen = (c) => (c?.anschrift_hand ? [c.name, ...String(c.address || "").split("\n")].map((z) => (z || "").trim()).filter(Boolean) : null);
// A-020: gespeicherter Beleg mit von Hand gestalteter Anschrift
const frei = (doc) => !!doc.anschrift_frei || String(doc.customer_address || "").includes("\n");
// A-020 P2b: „Anschrift anpassen“ benutzt? (ältere Belege ohne Kennzeichen: mehrzeilig ohne Adresszeilen beim Kunden)
const vonHand = (doc, kunde) => doc.anschrift_hand ?? (frei(doc) && !anschriftBesonders(kunde || {}));
// A-020 P2b: Beleg noch nicht verschickt → folgt den Kundendaten (Angebot/Rechnung: Entwurf; AB: Offen/In Arbeit)
const nochNichtVerschickt = (type, status) => (type === "order" ? ["", "Offen", "In Arbeit"] : ["", "Entwurf"]).includes(status || "");
const zeilenVon = (c) => (c?.anschrift_frei ? [c.name, ...String(c.address || "").split("\n")] : [c?.firma, ...String(c?.name || "").split("\n"), ...String(c?.address || "").split(/,\s*/)])
  .map((z) => (z || "").trim()).filter((z, i, a) => z && a.indexOf(z) === i).join("|");
const gleicheAnschrift = (a, b) => zeilenVon(a) === zeilenVon(b);

const WysiwygDocumentEditor = ({ type = "quote" }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const isNew = !id || id === "new";
  // Vorlage bearbeiten: der Beleg ist ein Arbeits-Beleg zu einer Dokument-Vorlage (?vorlage=<id>), ohne Nummer/Kunde/Versand
  const vorlageId = new URLSearchParams(location.search).get("vorlage") || "";
  const vorlageModus = !!vorlageId && !isNew;
  const [vorlageName, setVorlageName] = useState("");
  const [alsNeuName, setAlsNeuName] = useState(null); // null = Fenster zu
  const VORLAGEN_LISTE = "/settings?tab=doc-templates";
  const [neueAnschrift, setNeueAnschrift] = useState(null);  // A-020 P2b: Kundendaten weichen vom gespeicherten Beleg ab

  // Konvertiert HTML-Reste (<p>, &nbsp;, Umlaut-Entitaeten) zurueck in Plain-Text,
  // damit der Vortext/Schlusstext im Textfeld sauber und komplett lesbar ist.
  const htmlToPlain = (html) => {
    if (!html) return "";
    if (!/<[a-z][^>]*>|&[a-z#0-9]+;/i.test(html)) return html;
    let t = String(html)
      .replace(/<p>\s*---\s*<\/p>/gi, "\n---\n")
      .replace(/<br\s*\/?>/gi, "\n")
      .replace(/<\/p>\s*<p>/gi, "\n\n")
      .replace(/<p>/gi, "")
      .replace(/<\/p>/gi, "")
      .replace(/<[^>]+>/g, "");
    // HTML-Entitaeten dekodieren
    try {
      const tmp = document.createElement("textarea");
      tmp.innerHTML = t;
      t = tmp.value;
    } catch { /* no-op */ }
    return t.replace(/\u00A0/g, " ").trim();
  };
  
  const [customers, setCustomers] = useState([]);
  const [articles, setArticles] = useState([]);
  const [services, setServices] = useState([]);
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [ladeRunde, setLadeRunde] = useState(0); // zaehlt abgeschlossene Ladevorgaenge (Aenderungserkennung)
  const [saving, setSaving] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [settingsTab, setSettingsTab] = useState("settings");
  
  // Document state
  const [selectedCustomerId, setSelectedCustomerId] = useState("");
  const [customer, setCustomer] = useState(null);
  // A-015: Projekt des Belegs (null = alter Beleg ohne Angabe, "" = ohne Projekt); fest = aus dem Projekt angelegt
  const [projektId, setProjektId] = useState(null);
  const [projektFest, setProjektFest] = useState(false);
  useF1Help("hilfe_dokumente"); // A-015: F1 erklärt u. a. die Projekt-Zuordnung
  const geladenesProjektRef = useRef(null);
  // A-015b: Projekte des Kunden einmal laden (Schild in der Leiste, Bauvorhaben-Zeile, Betreff-Vorbelegung)
  const [projektNeu, setProjektNeu] = useState(0); // A-020 P4: nach Stift „Bauvorhaben“ neu laden
  const projektDaten = useBelegProjekte(selectedCustomerId, projektNeu);
  const aktuellesProjekt = projektId ? (projektDaten?.projekte || []).find((p) => p.id === projektId) || null : null;
  const projektWaehlenAusUrl = new URLSearchParams(location.search).get("projekt_waehlen") === "1";
  const [positions, setPositions] = useState([
    { type: "position", pos_nr: 1, description: "", quantity: 1, unit: "Stück", price_net: 0 }
  ]);
  const [notes, setNotes] = useState("");
  const [vortext, setVortext] = useState("");
  const [schlusstext, setSchlusstext] = useState("");
  const [betreff, setBetreff] = useState("");
  // A-015b: neuer Beleg ohne Angabe → einziges offenes Projekt vorauswählen (vorher im Feld, jetzt im Editor,
  // weil die Auswahlliste erst beim Klick auf das Projekt-Schild erscheint)
  useEffect(() => {
    if (!isNew || projektFest || projektId !== null || !projektDaten) return;
    setProjektId(projektDaten.vorschlag || "");
  }, [projektDaten, projektId, isNew, projektFest]);
  // A-015b: neuer Beleg mit Projekt → Betreff = Projekttitel (nur wenn der Betreff leer ist)
  useEffect(() => {
    if (isNew && aktuellesProjekt && !betreff.trim()) setBetreff(aktuellesProjekt.titel);
  }, [aktuellesProjekt?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  // KI-Textkorrektur: aktives Feld {kontext, label, text, setter} oder null
  const [korrekturTarget, setKorrekturTarget] = useState(null);
  const [vatRate, setVatRate] = useState(19);
  const [discount, setDiscount] = useState(0);
  const [discountType, setDiscountType] = useState("percent");
  const [status, setStatus] = useState(type === "quote" ? "Entwurf" : "Offen");
  const [depositAmount, setDepositAmount] = useState(0);
  const [depositNote, setDepositNote] = useState("");
  // Anzahlung (05.10.2026): Vorschlag-Hinweis (AB), Stand aus dem Backend, Abzuege der Schlussrechnung
  const [depositVorschlag, setDepositVorschlag] = useState(type === "order" && (!id || id === "new"));
  const [anzahlungStand, setAnzahlungStand] = useState(null);
  const [anzahlungReload, setAnzahlungReload] = useState(0);
  const [anzahlungsAbzuege, setAnzahlungsAbzuege] = useState([]);
  const [dueDays, setDueDays] = useState(null); // null = noch nicht aus Settings/DB geladen
  const [docNumber, setDocNumber] = useState("");
  const [abNummer, setAbNummer] = useState(""); // A-022: Nummer der AB, aus der diese Rechnung entstand (Verknüpfung order_id)
  // A-023: Rechnung ist Entwurf ohne Nummer, bis sie festgeschrieben wird; Übungsbeleg (UEB-…) zählt nirgends mit
  const [uebungsbeleg, setUebungsbeleg] = useState(false);
  const [belegTest, setBelegTest] = useState(false);  // A-024: Beleg eines Testkunden (Server: uebung_grund „testkunde“)
  const [belegInfo, setBelegInfo] = useState({});
  const [createdAt, setCreatedAt] = useState(new Date().toISOString());
  const [validUntil, setValidUntil] = useState(null);
  const [versendetAm, setVersendetAm] = useState(null);
  
  // Voice recording
  const [isRecording, setIsRecording] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);

  // Sidebar & UI state
  const [sidebarSearch, setSidebarSearch] = useState("");
  const [sidebarTab, setSidebarTab] = useState("services");
  const [costPrices, setCostPrices] = useState({});
  const [selectedItem, setSelectedItem] = useState(null);
  const [templateDocs, setTemplateDocs] = useState([]);
  const [similarDocs, setSimilarDocs] = useState([]);
  const [expandedDoc, setExpandedDoc] = useState(null);
  const [activeKalkIdx, setActiveKalkIdx] = useState(null);
  const [titelTemplates, setTitelTemplates] = useState([]);
  const [titelDropdownIdx, setTitelDropdownIdx] = useState(null);
  const [stammChangeIdx, setStammChangeIdx] = useState(null);
  const [leistungsBloecke, setLeistungsBloecke] = useState([]);
  const [selectedPositions, setSelectedPositions] = useState(new Set());
  const [blockSaveName, setBlockSaveName] = useState("");
  const [dragIndex, setDragIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [saveAsArticleIdx, setSaveAsArticleIdx] = useState(null);
  const [saveAsType, setSaveAsType] = useState("Leistung");

  const [showPreview, setShowPreview] = useState(false);
  const [showLohnanteil, setShowLohnanteil] = useState(type === "invoice");
  const [copying, setCopying] = useState(false);
  const [lohnanteilCustom, setLohnanteilCustom] = useState("");
  const [showLohnkosten, setShowLohnkosten] = useState(false);
  const [showLeistungenPanel, setShowLeistungenPanel] = useState(false);
  const [texteVorher, setTexteVorher] = useState(null);  // A-021b: Text der Quelle (Angebot/AB), wenn Standardtexte eingesetzt wurden
  const [showDocCheck, setShowDocCheck] = useState(false);
  const [showPdfPreview, setShowPdfPreview] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(100);

  const titles = { quote: "Angebot", order: "Auftragsbestätigung", invoice: "Rechnung" };
  const listPaths = { quote: "/quotes", order: "/orders", invoice: "/invoices" };
  const docTypeMap = { quote: "angebot", order: "auftrag", invoice: "rechnung" };

  // ==================== DATA LOADING ====================
  // Ablaufaufzeichnung (08.10.2026): ein Vorgang je geöffnetem Dokument; Ende = gespeichert / abgebrochen / ohne Änderung.
  // Steht vor dem Laden, damit schon die Ladeanfragen zum Vorgang gehören.
  const ablaufGespeichertRef = useRef(false);
  const ablaufDokIdRef = useRef(null);
  const ablaufKundeRef = useRef(null);
  useEffect(() => { ablaufKundeRef.current = selectedCustomerId || null; }, [selectedCustomerId]);
  useEffect(() => {
    const p = new URLSearchParams(location.search);
    ablaufGespeichertRef.current = false;
    ablaufDokIdRef.current = isNew ? null : id;
    const vid = starteVorgang(`${titles[type]} ${isNew ? "neu" : "öffnen"}`, { kunde_id: p.get("customer"), projekt_id: p.get("projekt_id"), dokument_id: isNew ? null : id, dokument_art: type });
    return () => beendeVorgang(vid, ablaufGespeichertRef.current ? "gespeichert" : isDirtyRef.current ? "abgebrochen" : "ohne_aenderung",
      { dokument_id: ablaufDokIdRef.current, kunde_id: ablaufKundeRef.current });
  }, [id, type]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { loadData(); }, [id]);

  const standardTextRef = useRef({});  // automatisch eingesetzter Standardtext je Feld {inhalt, text}
  const loadData = async () => {
    standardTextRef.current = {};
    try {
      const [settingsRes, kontaktRes, modulArtikelRes, kundenModulRes] = await Promise.all([
        api.get("/settings"),
        api.get("/modules/kontakt/data").catch(() => ({ data: [] })),
        api.get("/modules/artikel/data").catch(() => ({ data: [] })),
        api.get("/modules/kunden/data").catch(() => ({ data: [] }))
      ]);
      // NUR Modul-Daten verwenden (kein Legacy)
      const kontaktData = (kontaktRes.data || []).map(k => ({
        ...k,
        name: `${k.vorname || ""} ${k.nachname || ""}`.trim() || k.firma || "Unbekannt",
        address: `${k.strasse || ""} ${k.hausnummer || ""}, ${k.plz || ""} ${k.ort || ""}`.trim().replace(/^,\s*/, "").replace(/,\s*$/, ""),
        _source: "kontakt-modul"
      }));
      const kundenModulData = (kundenModulRes.data || []).map(k => ({
        ...k,
        name: `${k.vorname || ""} ${k.nachname || ""}`.trim() || k.firma || "Unbekannt",
        address: k.address || `${k.strasse || ""} ${k.hausnummer || ""}, ${k.plz || ""} ${k.ort || ""}`.trim().replace(/^,\s*/, "").replace(/,\s*$/, ""),
        _source: "kunden-modul"
      }));
      // Deduplizierung per E-Mail
      const seenEmails = new Set();
      const allModulCustomers = [...kundenModulData, ...kontaktData].filter(c => {
        if (!c.email) return true;
        const lower = c.email.toLowerCase();
        if (seenEmails.has(lower)) return false;
        seenEmails.add(lower);
        return true;
      });
      setCustomers(allModulCustomers);
      // NUR Modul-Artikel verwenden
      const modulArtikel = modulArtikelRes.data || [];
      setArticles(modulArtikel.filter(a => a.typ === "Artikel"));
      setServices(modulArtikel.filter(a => a.typ === "Leistung" || a.typ === "Fremdleistung"));
      setSettings(settingsRes.data);
      if (isNew && settingsRes.data?.default_vat_rate) {
        setVatRate(settingsRes.data.default_vat_rate);
      }
      // Zahlungsziel beim NEU-Anlegen aus Settings vorbelegen (Default 14)
      if (isNew && type === "invoice" && dueDays === null) {
        const def = settingsRes.data?.default_due_days;
        setDueDays(Number.isFinite(def) ? def : 14);
      }

      try { const titelRes = await api.get("/modules/textvorlagen/data", { params: { text_type: "titel", doc_type: "allgemein" } }); setTitelTemplates(titelRes.data); } catch {}
      try { const blockRes = await api.get("/leistungsbloecke"); setLeistungsBloecke(blockRes.data); } catch {}

      if (!isNew) {
        const endpoint = type === "quote" ? "quotes" : type === "order" ? "orders" : "invoices";
        const res = await api.get(`/${endpoint}/${id}`);
        const doc = res.data;
        setSelectedCustomerId(doc.customer_id);
        setCustomer({ name: doc.customer_name, address: doc.customer_address, id: doc.customer_id, anschrift_frei: frei(doc) });
        setProjektId(doc.projekt_id ?? null); setProjektFest(false); geladenesProjektRef.current = doc.projekt_id ?? null;
        // Kunden-Details frisch laden (E-Mail, Adresszeilen …); A-020 P2b: Entwurf folgt den Kundendaten, sonst Hinweis bei Abweichung
        if (doc.customer_id) {
          const listenKunde = allModulCustomers.find(c => c.id === doc.customer_id);
          let data = null;
          try {
            const kRes = await api.get(`/modules/kunden/data/${doc.customer_id}`).catch(() => null);
            data = kRes?.data || listenKunde || (await api.get(`/modules/kontakt/data/${doc.customer_id}`).catch(() => null))?.data;
          } catch {}
          if (data) {
            const hand = vonHand(doc, data);
            const aktuell = kundeFuerBeleg({ ...listenKunde, ...data, id: doc.customer_id });
            const gespeichert = { ...listenKunde, ...data, id: doc.customer_id, name: doc.customer_name, address: doc.customer_address, anschrift_frei: frei(doc), anschrift_hand: hand };
            if (nochNichtVerschickt(type, doc.status) && !hand) setCustomer(aktuell);
            else { setCustomer(gespeichert); if (!gleicheAnschrift(aktuell, gespeichert)) setNeueAnschrift(aktuell); }
          }
        }
        setPositions((doc.positions || []).map(p => ({ ...p, type: p.type || "position" })));
        setNotes(doc.notes || ""); setVortext(htmlToPlain(doc.vortext)); setSchlusstext(htmlToPlain(doc.schlusstext));
        setTexteVorher(doc.texte_vorher && (doc.texte_vorher.vortext || doc.texte_vorher.schlusstext) ? doc.texte_vorher : null);
        setBetreff(doc.betreff || ""); setVatRate(doc.vat_rate || 19);
        setDiscount(doc.discount || 0); setDiscountType(doc.discount_type || "percent");
        setStatus(doc.status || ""); setDepositAmount(type === "order" ? (doc.deposit_amount ?? null) : (doc.deposit_amount || 0));
        // Anzahlung (05.10.2026): Vorschlag 30 % nur bei neu angelegten ABs; bestehende ABs ohne
        // Anzahlung bleiben leer (Knopf "30 %" steht zur Verfuegung)
        setDepositNote(doc.deposit_note || "");
        setDepositVorschlag(type === "order" && doc.deposit_amount == null && !!doc.anzahlung_vorschlag);
        if (type === "invoice") setAnzahlungsAbzuege(doc.anzahlungsrechnungen || []);
        if (type === "invoice") {
          // Rückrechnen falls due_days fehlt: due_date - created_at
          let dd = doc.due_days;
          if (dd == null && doc.due_date && doc.created_at) {
            try {
              const a = new Date(doc.created_at);
              const b = new Date(doc.due_date);
              dd = Math.max(0, Math.round((b - a) / 86400000));
            } catch { dd = 14; }
          }
          setDueDays(Number.isFinite(dd) ? dd : 14);
        }
        setDocNumber(doc.quote_number || doc.order_number || doc.invoice_number);
        setAbNummer("");
        if (type === "invoice" && doc.order_id) {
          api.get(`/orders/${doc.order_id}`).then((r) => setAbNummer(r.data?.order_number || "")).catch(() => {});
        }
        setBelegTest(doc.uebungsbeleg === true && doc.uebung_grund === "testkunde");
        if (type === "invoice") {
          setUebungsbeleg(!!doc.uebungsbeleg);
          setBelegInfo({ art: doc.art || "", storno_zu_nummer: doc.storno_zu_nummer || "", storniert_durch_nummer: doc.storniert_durch_nummer || "", status: doc.status || "" });
        }
        ablaufAktion("Dokument geladen", { dokument_id: doc.id, dokument_art: type, kunde_id: doc.customer_id });
        setCreatedAt(doc.created_at);
        setValidUntil(doc.valid_until || null);
        setVersendetAm(doc.versendet_am || null);
        if (doc.show_lohnanteil) setShowLohnanteil(true);
        // Anzahlungsrechnung: Lohnanteil standardmaessig "nicht ausweisen" (06.10.2026)
        else if (doc.art === "anzahlung") setShowLohnanteil(false);
        // BUT-0030: gespeicherter Betrag = Automatik → weiter automatisch (folgt den Positionen); nur ein abweichender Betrag gilt als von Hand gesetzt
        if (doc.lohnanteil_custom) {
          const _dp = (doc.positions || []).map(p => ({ ...p, type: p.type || "position" }));
          const _ds = _dp.filter(p => p.type !== "titel").reduce((sum, p) => sum + (p.quantity || 0) * (p.price_net || 0), 0);
          const _dn = _ds - ((doc.discount_type || "percent") === "percent" ? _ds * ((Number(doc.discount) || 0) / 100) : (Number(doc.discount) || 0));
          const _auto = lohnSumme(lohnZeilen(_dp, modulArtikel, rabattFaktor(_ds, _dn)));
          if (Math.abs(Number(doc.lohnanteil_custom) - _auto) > 0.004) setLohnanteilCustom(doc.lohnanteil_custom);
        }
      } else {
        const params = new URLSearchParams(location.search);
        const preselectedCustomerId = params.get("customer");
        if (preselectedCustomerId) {
          const cust = allModulCustomers.find(c => c.id === preselectedCustomerId);
          if (cust) { setSelectedCustomerId(preselectedCustomerId); setCustomer(kundeFuerBeleg(cust)); }
        }
        // A-015: aus dem Projekt heraus angelegt → dieses Projekt fest
        const projektAusUrl = params.get("projekt_id");
        setProjektId(projektAusUrl || null); setProjektFest(!!projektAusUrl);
        // Standard-Vor-/Schlusstext der Belegart (Ralph 22.10.2026); folgt Kunde und Beträgen, bis er geändert wird
        const startKunde = preselectedCustomerId ? allModulCustomers.find(c => c.id === preselectedCustomerId) : null;
        const startZiel = type === "invoice" ? (Number.isFinite(settingsRes.data?.default_due_days) ? settingsRes.data.default_due_days : 14) : undefined;
        const [stdVor, stdSchluss] = await Promise.all([ladeStandardVorlage(docTypeMap[type], "vortext"), ladeStandardVorlage(docTypeMap[type], "schlusstext")]);
        for (const [feld, vorlage, setter] of [["vortext", stdVor, setVortext], ["schlusstext", stdSchluss, setSchlusstext]]) {
          if (!vorlage?.content) continue;
          const text = htmlToPlain(resolvePlaceholders(vorlage.content, startKunde ? kundeFuerBeleg(startKunde) : null, settingsRes.data, "", null, { due_days: startZiel }));
          standardTextRef.current[feld] = { inhalt: vorlage.content, text };
          setter(text);
        }
      }
    } catch { toast.error("Fehler beim Laden der Daten"); } finally { setLoading(false); setLadeRunde((n) => n + 1); }
  };

  // ==================== LOHNANTEIL ====================
  // BUT-0030: Lohn je Position automatisch aus Art der Position (lib/lohnanteil.js, eine Regel); Hand geht vor
  const artikelAlle = [...articles, ...services];
  const _lohnSub = positions.filter(p => p.type !== "titel").reduce((sum, p) => sum + (p.quantity || 0) * (p.price_net || 0), 0);
  const _lohnNach = _lohnSub - (discountType === "percent" ? _lohnSub * ((Number(discount) || 0) / 100) : (Number(discount) || 0));
  const lohnZeilenListe = lohnZeilen(positions, artikelAlle, rabattFaktor(_lohnSub, _lohnNach));
  const totalLaborCost = lohnSumme(lohnZeilenListe);
  const lohnHandSetzen = (idx, betrag) => setPositions(ps => ps.map((p, i) => (i === idx ? positionMitHandLohn(p, betrag) : p)));
  const lohnAutomatik = (idx) => setPositions(ps => ps.map((p, i) => (i === idx ? positionAutomatisch(p, artikelAlle) : p)));
  const effectiveLohnanteil = lohnanteilCustom !== "" ? parseFloat(lohnanteilCustom) || 0 : totalLaborCost;
  const lohnanteilMwst = effectiveLohnanteil * (vatRate / 100);
  const lohnanteilBrutto = effectiveLohnanteil + lohnanteilMwst;

  // ==================== HANDLERS ====================
  const handleCustomerChange = (customerId, neuerKunde = null) => {
    setSelectedCustomerId(customerId);
    setNeueAnschrift(null);
    // A-015: anderer Kunde → Projekt neu wählen (neuer Beleg: Vorauswahl läuft erneut)
    if (customerId !== selectedCustomerId) { setProjektId(isNew ? null : ""); setProjektFest(false); }
    const cust = neuerKunde || customers.find(c => c.id === customerId) || null;
    if (cust) {
      // Adresse zusammenbauen falls Einzelfelder vorhanden
      setCustomer(kundeFuerBeleg(cust));
      // A-020 P2b: Kundenliste wurde beim Öffnen geladen → frische Kundendaten holen (z. B. neue Adresszeilen)
      if (!neuerKunde && cust._source === "kunden-modul") {
        api.get(`/modules/kunden/data/${customerId}`).then((r) => {
          if (r.data) setCustomer((c) => (c?.id === customerId ? kundeFuerBeleg({ ...cust, ...r.data }) : c));
        }).catch(() => {});
      }
    } else {
      setCustomer(null);
    }
  };

  const addPosition = () => setPositions([...positions, { type: "position", pos_nr: 0, description: "", quantity: 1, unit: "Stück", price_net: 0, labor_cost: 0 }]);
  const addTitel = () => setPositions([...positions, { type: "titel", pos_nr: 0, description: "" }]);

  const saveTitelTemplate = async (name) => {
    if (!name?.trim() || titelTemplates.some(t => t.content === name.trim())) return;
    try {
      await api.post("/modules/textvorlagen/data", { doc_type: "allgemein", text_type: "titel", title: name.trim(), content: name.trim() });
      const res = await api.get("/modules/textvorlagen/data", { params: { text_type: "titel", doc_type: "allgemein" } });
      setTitelTemplates(res.data); toast.success("Titel-Vorlage gespeichert!");
    } catch { toast.error("Fehler beim Speichern"); }
  };

  const togglePositionSelect = (idx) => {
    setSelectedPositions(prev => { const next = new Set(prev); if (next.has(idx)) next.delete(idx); else next.add(idx); return next; });
  };

  const saveAsLeistungsBlock = async () => {
    if (!blockSaveName.trim() || selectedPositions.size === 0) return;
    const blockPositions = [...selectedPositions].sort((a, b) => a - b).map(idx => {
      const p = positions[idx];
      return { type: p.type, description: p.description, quantity: p.quantity || 0, unit: p.unit || "Stück", price_net: p.price_net || 0 };
    });
    try {
      await api.post("/leistungsbloecke", { name: blockSaveName.trim(), positions: blockPositions });
      const res = await api.get("/leistungsbloecke"); setLeistungsBloecke(res.data);
      setSelectedPositions(new Set()); setBlockSaveName(""); toast.success("Baustein gespeichert!");
    } catch { toast.error("Fehler beim Speichern"); }
  };

  // Bausteine-Profi: ganzer Baustein oder nur die markierten Positionen (liste); Nummern ergeben sich aus der Reihenfolge (getNumbering), Titel bleiben Titel
  const insertLeistungsBlock = (block, liste) => {
    const teil = liste || block.positions;
    setPositions([...positions, ...fuerBeleg(teil, positions.length)]);
    toast.success(`Baustein "${block.name}" eingefügt (${teil.length} Positionen)`);
    return true;
  };

  // Baustein-Inhalt ändern im gewohnten Beleg: markierte Positionen ersetzen den Inhalt des gewählten Bausteins (ID und Name bleiben)
  const ersetzeLeistungsBlock = async (blockId) => {
    const block = leistungsBloecke.find((b) => b.id === blockId);
    if (!block || selectedPositions.size === 0) return;
    const neu = [...selectedPositions].sort((a, b) => a - b).map((idx) => {
      const p = positions[idx];
      return { type: p.type, description: p.description, quantity: p.quantity || 0, unit: p.unit || "Stück", price_net: p.price_net || 0 };
    });
    if (!window.confirm(`Baustein „${block.name}“ wird durch ${neu.length} Positionen ersetzt.`)) return;
    try {
      await api.put(`/leistungsbloecke/${blockId}`, { positions: neu });
      const res = await api.get("/leistungsbloecke"); setLeistungsBloecke(res.data);
      setSelectedPositions(new Set()); setBlockSaveName(""); toast.success(`Baustein „${block.name}“ ersetzt`);
    } catch { toast.error("Fehler beim Ersetzen"); }
  };

  // Eigenes Bausteine-Fenster (zweiter Bildschirm) fügt hier ein; die Kopfzeile dort zeigt, in welchen Beleg
  useBelegEmpfang(
    ({ block, positionen, vorlage }) => {
      if (vorlage?.tpl) { vorlageVomFenster(vorlage.tpl); return; }   // eigenes Vorlagen-Fenster (/vorlagen)
      if (block && Array.isArray(positionen) && positionen.length) insertLeistungsBlock(block, positionen);
    },
    () => ({ label: `${titles[type]} ${docNumber || (type === "invoice" ? "(Entwurf)" : "(neu)")}`, art: type }),
  );
  const handyAnsicht = useSchmal(640);
  const [leisteBreite, setLeisteBreite] = useState(() => {
    try { const v = parseInt(window.localStorage.getItem("bausteine_leiste_breite"), 10); return Number.isFinite(v) ? v : 380; } catch { return 380; }
  });
  const leisteBreiteGueltig = () => Math.min(Math.max(leisteBreite, 320), Math.round(window.innerWidth * 0.7));
  const leisteZiehen = (e) => {
    e.preventDefault();
    const bewegen = (ev) => setLeisteBreite(Math.min(Math.max(ev.clientX, 320), Math.round(window.innerWidth * 0.7)));
    const ende = (ev) => {
      window.removeEventListener("pointermove", bewegen); window.removeEventListener("pointerup", ende);
      const w = Math.min(Math.max(ev.clientX, 320), Math.round(window.innerWidth * 0.7));
      setLeisteBreite(w);
      try { window.localStorage.setItem("bausteine_leiste_breite", String(w)); } catch { /* egal */ }
    };
    window.addEventListener("pointermove", bewegen); window.addEventListener("pointerup", ende);
  };

  const updatePosition = (index, field, value) => {
    const updated = [...positions]; updated[index][field] = value; setPositions(updated);
  };

  const checkStammChange = (idx) => {
    const pos = positions[idx]; if (!pos?.source_article_id) return;
    const changed = pos.description !== pos.original_description || pos.unit !== pos.original_unit || pos.price_net !== pos.original_price_net;
    if (changed) setStammChangeIdx(idx);
  };

  const saveToStamm = async (idx) => {
    const pos = positions[idx]; if (!pos?.source_article_id) return;
    try {
      const descParts = pos.description.split(" - ");
      const updateData = { name: descParts[0] || pos.description, description: descParts.slice(1).join(" - ") || "", unit: pos.unit, price_net: pos.price_net };
      await api.put(`/modules/artikel/data/${pos.source_article_id}`, updateData);
      const updated = [...positions];
      updated[idx].original_description = pos.description; updated[idx].original_unit = pos.unit; updated[idx].original_price_net = pos.price_net;
      setPositions(updated); toast.success("Stammdaten aktualisiert!");
      const artRes = await api.get("/modules/artikel/data");
      setArticles((artRes.data || []).filter(a => a.typ === "Artikel"));
      setServices((artRes.data || []).filter(a => a.typ === "Leistung" || a.typ === "Fremdleistung"));
    } catch { toast.error("Fehler beim Aktualisieren der Stammdaten"); }
    setStammChangeIdx(null);
  };

  const removePosition = (index) => { if (positions.length <= 1) return; setPositions(positions.filter((_, i) => i !== index)); };
  const movePosition = (fromIndex, toIndex) => {
    if (toIndex < 0 || toIndex >= positions.length) return;
    const updated = [...positions]; const [moved] = updated.splice(fromIndex, 1); updated.splice(toIndex, 0, moved); setPositions(updated);
  };

  const handleSavePositionAsArticle = async (idx) => {
    const pos = positions[idx];
    if (!pos.description?.trim()) { toast.error("Beschreibung erforderlich"); return; }
    try {
      const res = await api.post("/modules/artikel/data", { name: pos.description.trim(), typ: saveAsType, price_net: pos.price_net || 0, unit: pos.unit || "Stück" });
      toast.success(`Als ${saveAsType} gespeichert: ${res.data.artikel_nr}`);
      updatePosition(idx, "artikel_nr", res.data.artikel_nr); setSaveAsArticleIdx(null);
      const artRes = await api.get("/modules/artikel/data");
      setArticles((artRes.data || []).filter(a => a.typ === "Artikel"));
      setServices((artRes.data || []).filter(a => a.typ === "Leistung" || a.typ === "Fremdleistung"));
      setSidebarSearch("");
      setSidebarTab(saveAsType === "Artikel" ? "articles" : "services");
    } catch { toast.error("Fehler beim Speichern"); }
  };

  const addFromStamm = (item, atIndex) => {
    const desc = item.name + (item.description ? ` - ${item.description}` : "");
    const newPos = { type: "position", pos_nr: 0, description: desc, quantity: 1, unit: item.unit, price_net: item.price_net, labor_cost: item.labor_cost || 0, source_article_id: item.id, artikel_typ: item.typ || "", original_description: desc, original_unit: item.unit, original_price_net: item.price_net };
    const insertIdx = atIndex != null ? atIndex : positions.length;
    const updated = [...positions];
    updated.splice(insertIdx, 0, newPos);
    setPositions(updated);
    const ekPrice = item.ek_preis || item.purchase_price || 0;
    if (ekPrice > 0) {
      // Shift cost prices for positions after the insert point
      const newCosts = {};
      Object.entries(costPrices).forEach(([k, v]) => {
        const idx = parseInt(k);
        if (idx >= insertIdx) newCosts[idx + 1] = v;
        else newCosts[idx] = v;
      });
      newCosts[insertIdx] = ekPrice;
      setCostPrices(newCosts);
    }
  };

  const handleDragStart = (e, item) => { e.dataTransfer.setData("application/json", JSON.stringify(item)); e.dataTransfer.effectAllowed = "copy"; };

  const handleApplyKalkPrice = async (item, newPrice, newEk) => {
    if (!item?.id) return;
    try {
      await api.put(`/modules/artikel/data/${item.id}`, { price_net: newPrice, ek_preis: newEk });
      toast.success(`VK-Preis für "${item.name}" auf ${newPrice.toFixed(2)} € aktualisiert`);
      const artRes = await api.get("/modules/artikel/data");
      setArticles((artRes.data || []).filter(a => a.typ === "Artikel"));
      setServices((artRes.data || []).filter(a => a.typ === "Leistung" || a.typ === "Fremdleistung"));
    } catch { toast.error("Fehler beim Aktualisieren"); }
  };
  const openPositionKalk = (idx) => {
    const pos = positions[idx];
    if (!pos || pos.type === "titel") return;
    setActiveKalkIdx(idx);
  };

  const getActiveKalkItem = () => {
    if (activeKalkIdx == null) return null;
    const pos = positions[activeKalkIdx];
    if (!pos || pos.type === "titel") return null;
    if (pos.source_article_id) {
      const allItems = [...articles, ...services];
      const found = allItems.find(a => a.id === pos.source_article_id);
      if (found) return { ...found, _posIdx: activeKalkIdx };
    }
    const descParts = (pos.description || "").split("\n")[0].split(" - ");
    return {
      id: null,
      name: descParts[0] || `Position ${numbering[activeKalkIdx]}`,
      price_net: pos.price_net || 0,
      ek_preis: costPrices[activeKalkIdx] || 0,
      _posIdx: activeKalkIdx,
      _noSource: true,
    };
  };

  const handleKalkApply = (item, newPrice, newEk) => {
    if (item?._posIdx != null) {
      updatePosition(item._posIdx, "price_net", newPrice);
      if (newEk > 0) setCostPrices(prev => ({ ...prev, [item._posIdx]: newEk }));
    }
  };

  const handleSaveKalkToStamm = async (item, kalkData) => {
    if (!item?.id) return;
    try {
      await api.put(`/modules/artikel/data/${item.id}`, {
        price_net: kalkData.vkPreis,
        ek_preis: kalkData.ek,
      });
      await api.post("/kalkulation", {
        article_id: item.id, article_name: item.name, ek: kalkData.ek,
        zeit_meister: kalkData.zeitMeister, zeit_geselle: kalkData.zeitGeselle,
        zeit_azubi: kalkData.zeitAzubi, zeit_helfer: kalkData.zeitHelfer,
        rate_meister: kalkData.meisterRate, rate_geselle: kalkData.geselleRate,
        rate_azubi: kalkData.azubiRate, rate_helfer: kalkData.helferRate,
        sonstige_kosten: kalkData.sonstige, materialzuschlag: kalkData.materialzuschlag,
        gewinnaufschlag: kalkData.gewinnaufschlag, lohnkosten: kalkData.lohnkosten,
        sonstige_summe: kalkData.sonstigeSum, zwischensumme: kalkData.zwischensumme,
        material_betrag: kalkData.materialBetrag, gewinn_betrag: kalkData.gewinnBetrag,
        vk_preis: kalkData.vkPreis,
      });
      toast.success("Stammdaten aktualisiert");
      loadData();
    } catch { toast.error("Fehler beim Speichern"); }
  };

  const handleCreateFromKalk = async (kalkData, typ) => {
    const pos = activeKalkIdx != null ? positions[activeKalkIdx] : null;
    if (!pos) return;
    const name = (pos.description || "").split("\n")[0].trim();
    try {
      const res = await api.post("/modules/artikel/data", {
        name, typ, price_net: kalkData.vkPreis, ek_preis: kalkData.ek,
        unit: pos.unit || (typ === "Leistung" ? "Stunde" : "Stück"),
        description: pos.description || "",
      });
      const newArticle = res.data;
      const newId = newArticle.id;
      if (newId) {
        await api.post("/kalkulation", {
          article_id: newId, article_name: name, ek: kalkData.ek,
          zeit_meister: kalkData.zeitMeister, zeit_geselle: kalkData.zeitGeselle,
          zeit_azubi: kalkData.zeitAzubi, zeit_helfer: kalkData.zeitHelfer,
          rate_meister: kalkData.meisterRate, rate_geselle: kalkData.geselleRate,
          rate_azubi: kalkData.azubiRate, rate_helfer: kalkData.helferRate,
          sonstige_kosten: kalkData.sonstige, materialzuschlag: kalkData.materialzuschlag,
          gewinnaufschlag: kalkData.gewinnaufschlag, lohnkosten: kalkData.lohnkosten,
          sonstige_summe: kalkData.sonstigeSum, zwischensumme: kalkData.zwischensumme,
          material_betrag: kalkData.materialBetrag, gewinn_betrag: kalkData.gewinnBetrag,
          vk_preis: kalkData.vkPreis,
        });
        updatePosition(activeKalkIdx, "source_article_id", newId);
        // Immediately add article to local state to avoid race condition
        if (typ === "Artikel") {
          setArticles(prev => [...prev, newArticle]);
        } else {
          setServices(prev => [...prev, newArticle]);
        }
        toast.success(`${typ} "${name}" angelegt und verknüpft`);
        loadData();
      }
    } catch { toast.error("Fehler beim Anlegen"); }
  };

  const handleDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = "copy"; };
  const handleDrop = (e) => { e.preventDefault(); try { const item = JSON.parse(e.dataTransfer.getData("application/json")); addFromStamm(item); toast.success(`"${item.name}" hinzugefügt`); } catch {} };
  const updateCostPrice = (idx, value) => setCostPrices(prev => ({ ...prev, [idx]: parseFloat(value) || 0 }));

  const getPreviewDocument = () => ({
    id, customer_name: customer?.name || "", customer_address: customer?.address || "",
    customer_email: customer?.email || "", customer_id: customer?.id || "",
    quote_number: type === "quote" ? docNumber : undefined,
    order_number: type === "order" ? docNumber : undefined,
    invoice_number: type === "invoice" ? docNumber : undefined,
    betreff, vortext, schlusstext, status,
    positions, subtotal_net: subtotal, vat_rate: vatRate, vat_amount: vat, total_gross: total,
    discount, discount_type: discountType,
    created_at: new Date().toISOString(),
    valid_until: type === "quote" ? validUntil : undefined,
  });

  // ==================== COMPUTATIONS ====================
  const getNumbering = () => {
    let titelNr = 0, posInTitel = 0, hasTitel = positions.some(p => p.type === "titel"), flatNr = 0;
    return positions.map((p) => {
      if (p.type === "titel") { titelNr++; posInTitel = 0; return String(titelNr); }
      if (hasTitel) { posInTitel++; return titelNr > 0 ? `${titelNr}.${posInTitel}` : String(posInTitel); }
      flatNr++; return String(flatNr);
    });
  };
  const numbering = getNumbering();

  const getTitelGroups = () => {
    const groups = []; let currentGroup = null;
    positions.forEach((p, idx) => {
      if (p.type === "titel") { if (currentGroup) groups.push(currentGroup); currentGroup = { titel: p.description, nr: numbering[idx], sum: 0 }; }
      else if (currentGroup) { currentGroup.sum += (p.quantity || 0) * (p.price_net || 0); }
      else { if (!groups.length || groups[groups.length - 1].titel !== "__ungrouped") groups.push({ titel: "__ungrouped", nr: "", sum: 0 }); groups[groups.length - 1].sum += (p.quantity || 0) * (p.price_net || 0); }
    });
    if (currentGroup) groups.push(currentGroup);
    return groups.filter(g => g.titel !== "__ungrouped" || g.sum > 0);
  };

  const hasTitels = positions.some(p => p.type === "titel");

  const filteredServices = useMemo(() => services.filter(a =>
    a.name.toLowerCase().includes(sidebarSearch.toLowerCase()) || (a.description || "").toLowerCase().includes(sidebarSearch.toLowerCase())
  ), [services, sidebarSearch]);

  const filteredArticles = useMemo(() => articles.filter(a =>
    a.typ === "Artikel" && (a.name.toLowerCase().includes(sidebarSearch.toLowerCase()) || (a.description || "").toLowerCase().includes(sidebarSearch.toLowerCase()))
  ), [articles, sidebarSearch]);

  const calculateTotals = () => {
    const subtotal = positions.filter(p => p.type !== "titel").reduce((sum, p) => sum + (p.quantity || 0) * (p.price_net || 0), 0);
    const discountAmt = discountType === "percent" ? subtotal * (discount / 100) : discount;
    const netAfterDiscount = subtotal - discountAmt;
    const vatAmt = netAfterDiscount * (vatRate / 100);
    const total = netAfterDiscount + vatAmt;
    return { subtotal, discountAmt, netAfterDiscount, vat: vatAmt, total, finalAmount: total - depositAmount };
  };

  const calculateKalkulation = () => {
    const revenue = positions.reduce((sum, p) => sum + (p.quantity || 0) * (p.price_net || 0), 0);
    const costs = positions.reduce((sum, p, idx) => sum + (p.quantity || 0) * (costPrices[idx] || 0), 0);
    const margin = revenue - costs;
    return { revenue, costs, margin, marginPercent: revenue > 0 ? (margin / revenue) * 100 : 0, hasCosts: Object.values(costPrices).some(v => v > 0) };
  };

  // ==================== SUGGESTIONS ====================
  const loadSuggestions = async () => {
    try {
      const currentDescs = positions.filter(p => p.description).map(p => p.description).join(",");
      const res = await api.get(`/documents/suggestions/${type}?customer_id=${selectedCustomerId}&current_positions=${encodeURIComponent(currentDescs)}`);
      setTemplateDocs(res.data.templates || []); setSimilarDocs(res.data.similar || []);
    } catch {}
  };
  useEffect(() => { if (!loading) loadSuggestions(); }, [loading, selectedCustomerId]);

  const copyPositionsFromDoc = (doc) => {
    if (doc.positions?.length > 0) {
      const newPositions = doc.positions.map((p, idx) => ({ ...p, pos_nr: positions.length + idx + 1 }));
      setPositions([...positions, ...newPositions]); toast.success(`${newPositions.length} Position(en) übernommen`);
    }
  };

  const toggleDocTemplate = async (docId) => {
    try { const res = await api.put(`/documents/${type}/${docId}/template`); toast.success(res.data.is_template ? "Als Vorlage markiert" : "Vorlage entfernt"); loadSuggestions(); } catch {}
  };

  // ==================== VOICE ====================
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, { mimeType: "audio/webm" });
      mediaRecorderRef.current = mediaRecorder; chunksRef.current = [];
      mediaRecorder.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(chunksRef.current, { type: "audio/webm" });
        stream.getTracks().forEach(t => t.stop()); await processAudio(audioBlob);
      };
      mediaRecorder.start(); setIsRecording(true); toast.info("Aufnahme gestartet - Sprechen Sie jetzt...");
    } catch { toast.error("Mikrofon-Zugriff verweigert"); }
  };

  const stopRecording = () => { if (mediaRecorderRef.current && isRecording) { mediaRecorderRef.current.stop(); setIsRecording(false); } };

  const processAudio = async (audioBlob) => {
    if (!selectedCustomerId) { toast.error("Bitte wählen Sie zuerst einen Kunden aus"); return; }
    setAiLoading(true);
    try {
      const formData = new FormData(); formData.append("audio", audioBlob, "recording.webm");
      const sttRes = await axios.post(`${API}/speech-to-text`, formData, { headers: { "Content-Type": "multipart/form-data" } });
      const aiRes = await api.post("/ai/generate-quote", { customer_id: selectedCustomerId, transcribed_text: sttRes.data.text, vat_rate: vatRate });
      if (aiRes.data.positions?.length > 0) { setPositions(aiRes.data.positions); if (aiRes.data.notes) setNotes(aiRes.data.notes); toast.success("KI hat das Dokument erstellt!"); }
    } catch { toast.error("Fehler bei der Sprachverarbeitung"); } finally { setAiLoading(false); }
  };

  // ==================== SAVE / PDF / EMAIL / PRINT ====================
  const validateTextFields = () => {
    const problems = [];
    if (!betreff?.trim()) problems.push("Betreff ist leer");
    if (!vortext?.trim()) problems.push("Vortext ist leer");
    if (!schlusstext?.trim()) problems.push("Schlusstext ist leer");
    if (problems.length === 0) return true;
    return window.confirm(`Folgende Felder sind leer:\n\n${problems.join("\n")}\n\nTrotzdem speichern?`);
  };

  const validatePositions = () => {
    const problems = [];
    positions.filter(p => p.type !== "titel").forEach((p, idx) => {
      if (!p.description?.trim()) return;
      if (!p.price_net || p.price_net === 0) problems.push(`Position ${idx + 1}: "${p.description.substring(0, 45)}${p.description.length > 45 ? "..." : ""}" hat keinen Preis (0,00 EUR)`);
      if (!p.quantity || p.quantity === 0) problems.push(`Position ${idx + 1}: "${p.description.substring(0, 45)}${p.description.length > 45 ? "..." : ""}" hat Menge 0`);
    });
    if (problems.length === 0) return true;
    const msg = `Plausibilitätsprüfung - ${problems.length} Auffälligkeit${problems.length !== 1 ? "en" : ""} gefunden:\n\n${problems.slice(0, 6).join("\n")}${problems.length > 6 ? `\n... und ${problems.length - 6} weitere` : ""}\n\nWirklich trotzdem fortfahren?`;
    return window.confirm(msg);
  };

  const [showTemplateDialog, setShowTemplateDialog] = useState(false);
  const [showLoadTemplate, setShowLoadTemplate] = useState(false);
  const [vorlageFrage, setVorlageFrage] = useState(null);   // Vorlage aus dem eigenen Fenster wartet auf „Anhängen/Ersetzen“

  // Neuer Weg „Vorlage öffnen“ (01.11.2026): übernimmt Positionen (+ Rabatt) und – wenn gewünscht (Standard) und in der Vorlage vorhanden –
  // Vor- und Schlusstext aus der Vorlage; Betreff, Kunde, Anschrift, Nummer, Status und Datum bleiben unverändert. modus: "ersetzen" | "anhaengen".
  // Platzhalter ({anrede_brief} …) werden sofort mit dem Kunden des Belegs aufgelöst (ohne Kunde bleiben sie stehen, beim Speichern löst das Backend auf).
  // Der übernommene Text zählt als „von Hand“: standardTextRef wird geleert, damit kein späterer Kundenwechsel ihn überschreibt.
  const uebernehmeVorlagePositionen = (tpl, modus, mitTexte = true) => {
    const snap = tpl.snapshot || {};
    const neue = (Array.isArray(snap.positions) ? snap.positions : []).map(p => ({ ...p, type: p.type || "position" }));
    const leer = (p) => !(p.description || "").trim() && !(Number(p.price_net) > 0);
    const vorhanden = modus === "anhaengen" ? positions.filter(p => !leer(p)) : [];
    let nr = vorhanden.filter(p => p.type !== "titel").length;
    const nummeriert = neue.map(p => (p.type === "titel" ? { ...p, pos_nr: 0 } : { ...p, pos_nr: ++nr }));
    const neuePositionen = [...vorhanden, ...nummeriert];
    setPositions(neuePositionen);
    let neuRabatt = discount, neuRabattArt = discountType;
    if (modus !== "anhaengen" && snap.discount !== undefined) {   // Anhängen lässt den Rabatt des Belegs immer unverändert
      neuRabatt = snap.discount || 0; neuRabattArt = snap.discount_type || "percent";
      setDiscount(neuRabatt);
      setDiscountType(neuRabattArt);
    }
    if (mitTexte) {
      // Lohnanteil der Positionen NACH der Übernahme (gleiche Rechnung wie der Editor oben: lohnZeilen/lohnSumme/rabattFaktor; von Hand gesetzter Wert gilt)
      const sub = neuePositionen.filter(p => p.type !== "titel").reduce((sum, p) => sum + (p.quantity || 0) * (p.price_net || 0), 0);
      const nach = sub - (neuRabattArt === "percent" ? sub * ((Number(neuRabatt) || 0) / 100) : (Number(neuRabatt) || 0));
      const lohnNetto = lohnanteilCustom !== "" && lohnanteilCustom !== null && lohnanteilCustom !== undefined ? (Number(String(lohnanteilCustom).replace(",", ".")) || 0) : lohnSumme(lohnZeilen(neuePositionen, artikelAlle, rabattFaktor(sub, nach)));
      const lohnMwst = lohnNetto * (vatRate / 100);
      const lohnDaten = { netto: lohnNetto, mwst: lohnMwst, brutto: lohnNetto + lohnMwst, vatRate };
      for (const [feld, setter] of [["vortext", setVortext], ["schlusstext", setSchlusstext]]) {
        const roh = snap[feld];
        if (!roh || !String(htmlToPlain(roh)).trim()) continue;   // Vorlage ohne Text: Text des Belegs bleibt
        const klar = htmlToPlain(roh);
        setter(customer ? htmlToPlain(resolvePlaceholders(klar, customer, settings, docNumber, lohnDaten, { due_days: dueDays != null ? Number(dueDays) : undefined })) : klar);
        delete standardTextRef.current[feld];
      }
    }
    const n = neue.filter(p => p.type !== "titel").length;
    toast.success(`Vorlage „${tpl.name}“ übernommen – ${n} Position${n !== 1 ? "en" : ""}`);
    api.post(`/document-templates/${tpl.id}/verwendet`).catch(() => {});
    setShowLoadTemplate(false);
  };

  // Vorlage kommt aus dem eigenen Vorlagen-Fenster: leerer Beleg + passende Art = direkt, sonst Frage hier im Beleg (hier ist der Stand bekannt)
  const vorlageVomFenster = (tpl) => {
    if (vorlageModus) return;
    const hat = positions.some(p => (p.description || "").trim());
    if (tpl.doc_type === type && !hat) uebernehmeVorlagePositionen(tpl, "ersetzen", true);
    else setVorlageFrage(tpl);
  };

  // inaktiv seit 01.11.2026 (ersetzt durch uebernehmeVorlagePositionen), Entfernen nach Ralphs Prüfung
  const applyTemplate = (tpl) => {
    const snap = tpl.snapshot || {};
    if (Array.isArray(snap.positions) && snap.positions.length > 0) {
      setPositions(snap.positions.map(p => ({ ...p, type: p.type || "position" })));
    }
    if (snap.vortext !== undefined) setVortext(snap.vortext || "");
    if (snap.schlusstext !== undefined) setSchlusstext(snap.schlusstext || "");
    if (snap.betreff !== undefined) setBetreff(snap.betreff || "");
    if (snap.notes !== undefined) setNotes(snap.notes || "");
    if (snap.vat_rate !== undefined) setVatRate(snap.vat_rate);
    if (snap.discount !== undefined) setDiscount(snap.discount);
    if (snap.discount_type !== undefined) setDiscountType(snap.discount_type);
    if (snap.show_lohnanteil !== undefined) setShowLohnanteil(!!snap.show_lohnanteil);
    if (snap.lohnanteil_custom !== undefined) setLohnanteilCustom(snap.lohnanteil_custom);
    toast.success(`Vorlage "${tpl.name}" geladen - ${snap.positions?.length || 0} Position${snap.positions?.length !== 1 ? "en" : ""}`);
    setShowLoadTemplate(false);
  };

  const persistDocument = async (silent = false, anlass = null) => {
    // Automatisches Speichern legt ein NEUES Dokument erst an, wenn es eine Position mit Beschreibung hat
    // (08.10.2026: vorher entstand nach 60 s ein leeres Angebot mit eigener Nummer).
    if (anlass === AUTOSAVE_ANLASS && isNew && !positions.some(p => (p.description || "").trim())) return null;
    // Silent save - gibt savedId oder null zurueck, ohne Template-Dialog
    if (!selectedCustomerId && !vorlageModus) { toast.error("Achtung: Kein Kunde ausgewählt - Änderungen werden nicht gespeichert."); return null; }
    if (!silent) {
      if (!validateTextFields()) return null;
      if (!validatePositions()) return null;
    }
    setSaving(true);
    ablaufAktion(anlass || (silent ? "Speichern vor Vorschau/Senden" : "Speichern"), { dokument_id: isNew ? null : id, dokument_art: type, kunde_id: selectedCustomerId });
    try {
      const endpoint = type === "quote" ? "quotes" : type === "order" ? "orders" : "invoices";
      if (isNew) {
        const _lohn = lohnanteilCustom === "" || lohnanteilCustom === null ? (showLohnanteil ? totalLaborCost : null) : Number(String(lohnanteilCustom).replace(",", "."));
        const payload = { customer_id: selectedCustomerId, anschrift: anschriftZeilen(customer), positions: positions.filter(p => p.description), notes, vortext, schlusstext, betreff, ...(type === "order" && { deposit_note: depositNote }), discount, discount_type: discountType, vat_rate: vatRate, show_lohnanteil: showLohnanteil, lohnanteil_custom: (Number.isFinite(_lohn) ? _lohn : null), created_at: createdAt, ...(projektId !== null && { projekt_id: projektId }), ...(type === "quote" && { valid_days: settings?.default_quote_validity_days ?? 30 }), ...(type === "order" && depositAmount != null && !(depositVorschlag && !depositAmount) && { deposit_amount: depositAmount }), ...(type === "invoice" && { deposit_amount: depositAmount, ...(uebungsbeleg && { uebungsbeleg: true }), ...(dueDays != null && { due_days: Number(dueDays) }) }) };
        const res = await api.post(`/${endpoint}`, payload);
        if (res?.data?.id) { isDirtyRef.current = false; ablaufGespeichertRef.current = true; ablaufDokIdRef.current = res.data.id; navigate(`/${endpoint}/edit/${res.data.id}`, { replace: true }); return res.data.id; }
        return null;
      } else {
        const _lohn = lohnanteilCustom === "" || lohnanteilCustom === null ? (showLohnanteil ? totalLaborCost : null) : Number(String(lohnanteilCustom).replace(",", "."));
        const payload = { customer_id: selectedCustomerId, anschrift: anschriftZeilen(customer), positions: positions.filter(p => p.description), notes, vortext, schlusstext, betreff, ...(type === "order" && { deposit_note: depositNote }), discount, discount_type: discountType, vat_rate: vatRate, status, show_lohnanteil: showLohnanteil, lohnanteil_custom: (Number.isFinite(_lohn) ? _lohn : null), created_at: createdAt, ...(projektId !== geladenesProjektRef.current && { projekt_id: projektId ?? "" }), ...(type === "order" && depositAmount != null && !(depositVorschlag && !depositAmount) && { deposit_amount: depositAmount }), ...(type === "invoice" && { deposit_amount: depositAmount, ...(dueDays != null && { due_days: Number(dueDays) }) }) };
        if (vorlageModus) { for (const k of ["customer_id", "anschrift", "status", "projekt_id", "created_at"]) delete payload[k]; }  // Arbeits-Beleg: kein Kunde, kein Status, keine Automatik
        const putRes = await api.put(`/${endpoint}/${id}`, payload);
        if (type === "invoice" && putRes?.data?.invoice_number && putRes.data.invoice_number !== docNumber) setDocNumber(putRes.data.invoice_number);  // A-023: Status „Offen“ von Hand schreibt fest
        if (putRes?.data) {  // A-024: Kunde gewechselt → Übungszustand/Nummer folgen dem Kunden (Server entscheidet)
          const d = putRes.data;
          setBelegTest(d.uebungsbeleg === true && d.uebung_grund === "testkunde");
          if (type === "invoice") setUebungsbeleg(!!d.uebungsbeleg);
          const nr = d.quote_number || d.order_number || d.invoice_number || "";
          if (nr !== (docNumber || "")) setDocNumber(nr);
        }
        if ("projekt_id" in payload) geladenesProjektRef.current = payload.projekt_id;
        isDirtyRef.current = false;
        sauberStandRef.current = aktuellerStandRef.current;
        ablaufGespeichertRef.current = true;
        return id;
      }
    } catch { ablaufAktion("Speichern fehlgeschlagen", { dokument_id: isNew ? null : id, dokument_art: type }); if (!silent) toast.error("Fehler beim Speichern"); return null; } finally { setSaving(false); }
  };

  // BUT-0030: erscheint der Lohnanteil auf dem Beleg, bestätigt Ralph ihn vor Druck/PDF/Senden/Mail mit einem Klick (alle Wege rufen diese Funktion; Fenster: lib/lohnFrage.jsx)
  const lohnPanelZeigen = () => {
    if (typeof window !== "undefined" && window.innerWidth >= 1280) setShowLohnkosten(true);
    else setTimeout(() => document.querySelector('[data-testid="lohnkosten-unten"]')?.scrollIntoView({ behavior: "smooth", block: "center" }), 50);
  };
  const ensureLohnanteilOrConfirm = async () => {
    if (!showLohnanteil) return true;
    const vonHand = lohnanteilCustom !== "" && Math.abs(effectiveLohnanteil - totalLaborCost) > 0.004;
    // Was Ralph bestätigt, ist genau der Betrag, der gespeichert und gedruckt wird (bei 0 druckt das PDF nichts)
    const ja = await lohnanteilFragen(effectiveLohnanteil, vonHand ? "von Hand gesetzt" : lohnBeschreibung(lohnZeilenListe), lohnZeilenListe);
    if (!ja) lohnPanelZeigen();
    return ja;
  };

  // Vorlage bearbeiten (Ralph 31.10.2026): Beleg speichern, dann den Stand in die Vorlage übernehmen
  const vorlageSpeichern = async () => {
    const savedId = await persistDocument();
    if (!savedId) return null;
    try { await api.post(`/document-templates/${vorlageId}/vorlage-speichern`); toast.success(`Vorlage „${vorlageName}“ gespeichert`); return savedId; }
    catch { toast.error("Vorlage konnte nicht gespeichert werden"); return null; }
  };
  const vorlageAlsNeue = async () => {
    const name = (alsNeuName || "").trim();
    if (!name) { toast.error("Bitte einen Namen eingeben"); return; }
    const savedId = await persistDocument();
    if (!savedId) return;
    try { await api.post(`/document-templates/${vorlageId}/als-neue`, { name }); toast.success(`Neue Vorlage „${name}“ angelegt – die alte bleibt unverändert`); setAlsNeuName(null); }
    catch { toast.error("Neue Vorlage konnte nicht angelegt werden"); }
  };
  const vorlageSchliessen = async () => {
    if (isDirtyRef.current && !frageVerlassen()) return;
    isDirtyRef.current = false;
    try { await api.delete(`/document-templates/${vorlageId}/bearbeiten`); } catch { /* bleibt liegen, wird beim nächsten Öffnen wiederverwendet */ }
    navigate(VORLAGEN_LISTE, { replace: true, state: { ausBeleg: true } });
  };
  useEffect(() => {
    if (!vorlageModus) return;
    api.get(`/document-templates/${vorlageId}`).then((r) => setVorlageName(r.data?.name || "")).catch(() => {});
  }, [vorlageModus, vorlageId]);

  const handleSave = async () => {
    if (vorlageModus) return vorlageSpeichern();
    const savedId = await persistDocument();
    if (savedId) {
      toast.success(`${titles[type]} gespeichert!`);
      setTimeout(() => setShowTemplateDialog(savedId), 400);
    }
    return savedId;
  };

  const handleSaveAndExit = async () => { const savedId = await handleSave(); navigate(savedId && selectedCustomerId ? `/module/projekte/werkbank/${selectedCustomerId}` : listPaths[type], { replace: true, state: { ausBeleg: true } }); };

  // Nächster Schritt (A-021b, Ralph 22.10.2026): neuer Vorgang sofort anlegen und öffnen – gleicher Kunde, neue Nummer,
  // Vor-/Schlusstext = Standard der neuen Belegart (Server, utils/beleg_texte.py); kein Zwischenfenster mehr
  const copyTargetLabel = { order: "Auftragsbestätigung", "invoice-from-quote": "Rechnung", "invoice-from-order": "Rechnung" };
  const handleCopyTo = async (target) => {
    if (isNew || !id) { toast.error("Bitte speichern Sie zuerst das Dokument"); return; }
    setCopying(true);
    try {
      if (isDirtyRef.current && !(await persistDocument(true))) { toast.error("Dokument konnte nicht gespeichert werden"); return; }
      const cfg = {
        order: { url: `/documents/copy/quote-to-order/${id}`, open: "/orders/edit" },
        "invoice-from-quote": { url: `/documents/copy/quote-to-invoice/${id}`, open: "/invoices/edit" },
        "invoice-from-order": { url: `/documents/copy/order-to-invoice/${id}`, open: "/invoices/edit" },
      }[target];
      const res = await api.post(cfg.url, {});
      toast.success(`${copyTargetLabel[target]} erstellt`);
      navigate(`${cfg.open}/${res.data.id}`);
      window.scrollTo(0, 0);  // neuer Beleg beginnt oben (Anschrift, Vortext)
    } catch {
      toast.error("Erstellen fehlgeschlagen. Bitte erneut versuchen.");
    } finally {
      setCopying(false);
    }
  };

  const [showExitConfirm, setShowExitConfirm] = useState(false);
  const handleExit = () => { if (vorlageModus) { vorlageSchliessen(); return; } setShowExitConfirm(true); };

  // Autosave: speichert alle 60s im Hintergrund, still (kein Popup/Fehlertoast),
  // damit spontanes Wegnavigieren (z.B. schnell einen Termin anlegen) keine
  // Arbeit mehr verliert. Nutzt Refs, damit der Intervall-Timer nicht bei
  // jedem Tastendruck neu gestartet wird (sonst wuerde er nie abgelaufen).
  const persistDocumentRef = useRef(persistDocument);
  useEffect(() => { persistDocumentRef.current = persistDocument; });
  const savingRef = useRef(saving);
  useEffect(() => { savingRef.current = saving; }, [saving]);
  useEffect(() => {
    const interval = setInterval(() => {
      if (!savingRef.current) persistDocumentRef.current(true, AUTOSAVE_ANLASS);
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  // Merkt sich, ob seit dem letzten Laden/Speichern etwas geaendert wurde (fuer den Warnhinweis unten).
  // 08.10.2026: Vergleich mit dem Stand nach dem Laden. Vorher zaehlte jedes Setzen eines Wertes als
  // Aenderung – auch das Laden selbst –, sodass "Zurueck" schon beim blossen Ansehen fragte "speichern?".
  const isDirtyRef = useRef(false);
  const sauberStandRef = useRef(null);
  const aktuellerStand = JSON.stringify([positions, notes, vortext, schlusstext, betreff, discount, discountType, vatRate, showLohnanteil, lohnanteilCustom, status, depositAmount, depositNote, dueDays, isNew ? null : projektId]);
  const aktuellerStandRef = useRef(aktuellerStand);
  aktuellerStandRef.current = aktuellerStand;
  const ladeRundeRef = useRef(-1);
  useEffect(() => {
    if (loading) return;
    // Nach jedem abgeschlossenen Laden (auch nach Speichern eines neuen Dokuments -> /edit/<id>) neu vergleichen
    if (ladeRundeRef.current !== ladeRunde) { ladeRundeRef.current = ladeRunde; sauberStandRef.current = aktuellerStand; isDirtyRef.current = false; return; }
    isDirtyRef.current = aktuellerStand !== sauberStandRef.current;
  }, [aktuellerStand, loading, ladeRunde]);

  // Warnhinweis beim Schliessen/Neuladen des Browser-Tabs, wenn ungespeicherte
  // Aenderungen vorliegen (Autosave deckt spontanes Wegklicken innerhalb der
  // Suite bereits ab; dies hier ist die Absicherung fuer Tab zu/Neu laden).
  useEffect(() => {
    const handler = (e) => {
      if (isDirtyRef.current) { e.preventDefault(); e.returnValue = ""; }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (!isDirtyRef.current) return;
      const link = e.target.closest && e.target.closest("a[href]");
      if (!link) return;
      const href = link.getAttribute("href");
      if (!href || href.startsWith("#") || href.startsWith("http") || link.target === "_blank") return;
      e.preventDefault();
      e.stopPropagation();
      if (window.confirm("Ungespeicherte Änderungen - trotzdem verlassen?")) {
        isDirtyRef.current = false;
        navigate(href);
      }
    };
    document.addEventListener("click", handler, true);
    return () => document.removeEventListener("click", handler, true);
  }, [navigate]);
  const handleExitWithSave = async () => { setShowExitConfirm(false); const savedId = await handleSave(); navigate(savedId && selectedCustomerId ? `/module/projekte/werkbank/${selectedCustomerId}` : listPaths[type], { replace: true, state: { ausBeleg: true } }); };
  const handleExitWithSaveKunde = async () => { setShowExitConfirm(false); const savedId = await handleSave(); navigate(savedId && selectedCustomerId ? `/module/kunden?edit=${selectedCustomerId}` : listPaths[type], { replace: true, state: { ausBeleg: true } }); };
  const handleExitWithSaveProjekt = async () => { setShowExitConfirm(false); const savedId = await handleSave(); navigate(savedId && selectedCustomerId ? `/module/projekte/werkbank/${selectedCustomerId}` : listPaths[type], { replace: true, state: { ausBeleg: true } }); };
  const handleExitWithoutSave = () => { setShowExitConfirm(false); navigate(selectedCustomerId ? `/module/projekte/werkbank/${selectedCustomerId}` : listPaths[type], { replace: true, state: { ausBeleg: true } }); };
  // Zurueck-Button: bei ungespeicherten Aenderungen denselben Beenden-Dialog
  // oeffnen (EA-004, kein neuer Dialog), sonst direkt wie bisher navigieren.
  const handleBack = () => {
    if (vorlageModus) { vorlageSchliessen(); return; }
    if (isDirtyRef.current) { setShowExitConfirm(true); return; }
    navigate(selectedCustomerId ? `/module/projekte/werkbank/${selectedCustomerId}` : listPaths[type], { replace: true, state: { ausBeleg: true } });
  };

  // "Gültig bis" ab Versandtag: vor Drucken/Versand/Download festschreiben und den
  // Stand übernehmen – sonst schreibt das nächste Speichern das alte Datum zurück.
  const festschreibenFallsAngebot = async (savedId) => {
    if (type !== "quote") return true;
    try {
      const q = await angebotFestschreiben(savedId);
      setCreatedAt(q.created_at);
      setValidUntil(q.valid_until || null);
      setVersendetAm(q.versendet_am || null);
      return true;
    } catch {
      toast.error("Angebot konnte nicht festgeschrieben werden");
      return false;
    }
  };

  // Download und Drucken im PDF-Vorschaufenster zählen als Versand:
  // speichern, festschreiben, PDF neu holen
  const festgeschriebenesAngebotPdf = async () => {
    const savedId = await persistDocument();
    if (!savedId) return null;
    if (!(await festschreibenFallsAngebot(savedId))) return null;
    const res = await api.get(`/pdf/quote/${savedId}?t=${Date.now()}`, { responseType: "blob" });
    return new Blob([res.data], { type: "application/pdf" });
  };

  // A-023: Eine Rechnung ohne Nummer ist ein Entwurf. Drucken/Herunterladen als Original schreibt sie fest
  // (nach Rückfrage); die Vorschau bleibt Entwurf mit Aufdruck „ENTWURF“ und vergibt nichts.
  // A-024: Beleg eines Testkunden – gespeicherter Zustand oder (neu / Entwurf) der Haken am gewählten Kunden
  const testBeleg = belegTest || customer?.testkunde === true;
  const rechnungOhneNummer = () => type === "invoice" && !docNumber && !uebungsbeleg && !testBeleg && belegInfo.status !== "Storniert";
  const rechnungFestschreiben = async (savedId, anlass = "") => {
    if (!rechnungOhneNummer()) return true;
    let vorschau = "";
    try { vorschau = (await api.get("/v2/rechnungen/next-number-preview")).data.preview_number; } catch { /* ohne Vorschau weiter */ }
    const ok = window.confirm(
      `${anlass}Diese Rechnung hat noch keine Nummer (Entwurf).\n\n` +
      `Als Original bekommt sie jetzt die Nummer${vorschau ? " " + vorschau : ""} und ist festgeschrieben.\n\n` +
      `OK = Nummer vergeben und festschreiben\nAbbrechen = nichts verändern (zum Ansehen: „Vorschau“)`
    );
    if (!ok) return false;
    try {
      const r = await api.post(`/invoices/${savedId}/festschreiben`);
      setDocNumber(r.data.invoice_number);
      if (r.data.created_at) setCreatedAt(r.data.created_at);  // Rechnungsdatum = Festschreib-Datum
      if (r.data.status) { setStatus(r.data.status); setBelegInfo((b) => ({ ...b, status: r.data.status })); }
      toast.success(`Festgeschrieben als ${r.data.invoice_number}`);
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rechnung konnte nicht festgeschrieben werden");
      return false;
    }
  };
  // Download und Drucken im PDF-Vorschaufenster (Rechnung): erst festschreiben, dann das PDF als Original holen
  const festgeschriebenesRechnungPdf = async () => {
    const savedId = await persistDocument();
    if (!savedId) return null;
    if (!(await rechnungFestschreiben(savedId))) return null;
    const res = await api.get(`/pdf/invoice/${savedId}?t=${Date.now()}`, { responseType: "blob" });
    return new Blob([res.data], { type: "application/pdf" });
  };

  const handleDownloadPDF = async () => {
    if (!(await ensureLohnanteilOrConfirm())) return;
    // Immer zuerst speichern → PDF zeigt dann aktuellen Stand, nicht die alte Version
    const savedId = await persistDocument();
    if (!savedId) return;
    if (!(await rechnungFestschreiben(savedId))) return;
    try {
      const endpoint = type === "quote" ? "quote" : type === "order" ? "order" : "invoice";
      // Cache-Buster, damit Browser nicht versehentlich eine alte PDF-Version zeigt
      const res = await api.get(`/pdf/${endpoint}/${savedId}?t=${Date.now()}`, { responseType: "blob" });
      const namedFile = new File([res.data], `${titles[type]}_${docNumber}.pdf`, { type: "application/pdf" });
      const url = window.URL.createObjectURL(namedFile);
      const previewWin = window.open(url, "_blank");
      if (!previewWin) {
        // Fallback bei Popup-Blocker → Download
        const link = document.createElement("a"); link.href = url; link.setAttribute("download", `${titles[type]}_${docNumber}.pdf`);
        document.body.appendChild(link); link.click(); link.remove();
        toast.warning("Popup blockiert - PDF wurde stattdessen heruntergeladen");
      } else {
        toast.success("PDF wird in neuem Tab geöffnet");
      }
    } catch (e) { console.error("PDF error:", e); toast.error("Fehler beim PDF-Erzeugen: " + (e?.response?.statusText || e?.message || "unbekannt")); }
  };

  const handlePrint = async () => {
    if (!(await ensureLohnanteilOrConfirm())) return;
    // Immer zuerst speichern → Druck zeigt aktuellen Stand
    const savedId = await persistDocument();
    if (!savedId) return;
    if (!(await festschreibenFallsAngebot(savedId))) return;
    if (!(await rechnungFestschreiben(savedId))) return;
    try {
      const endpoint = type === "quote" ? "quote" : type === "order" ? "order" : "invoice";
      const res = await api.get(`/pdf/${endpoint}/${savedId}?t=${Date.now()}`, { responseType: "blob" });
      const namedFile = new File([res.data], `${titles[type]}_${docNumber}.pdf`, { type: "application/pdf" });
      const url = window.URL.createObjectURL(namedFile);
      const printWindow = window.open(url, "_blank");
      if (printWindow) {
        printWindow.addEventListener("load", () => {
          setTimeout(() => { printWindow.print(); }, 500);
        });
      } else {
        // Fallback: iframe print
        const iframe = document.createElement("iframe");
        iframe.style.display = "none";
        iframe.src = url;
        document.body.appendChild(iframe);
        iframe.addEventListener("load", () => {
          setTimeout(() => { iframe.contentWindow.print(); }, 500);
        });
      }
    } catch { toast.error("Fehler beim Drucken"); }
  };

  // "Senden" (05.10.2026): Klick 1 Mail-Vorschau, Klick 2 "Wirklich senden?", dann Versand
  // über die Suite (SMTP, Kopie in "Gesendet", Protokoll, Status). Betterbird entfällt.
  const [sendVorschau, setSendVorschau] = useState(null);
  const [sendBestaetigen, setSendBestaetigen] = useState(false);
  const [sending, setSending] = useState(false);
  const [weitereWege, setWeitereWege] = useState(false);
  const lohnanteilFuerText = { netto: effectiveLohnanteil, mwst: lohnanteilMwst, brutto: lohnanteilBrutto, vatRate };
  const anzahlungOffen = Math.max(0, Math.round((Number(depositAmount || 0) - Number(anzahlungStand?.erhalten || 0)) * 100) / 100);
  const docContextFuerText = { due_days: dueDays != null ? Number(dueDays) : undefined, anzahlung: depositAmount, anzahlung_offen: anzahlungOffen };
  // Standardtext im neuen Beleg folgt Kunde, Lohnanteil, Zahlungsziel – solange er nicht von Hand geändert wurde
  useEffect(() => {
    const nachziehen = (feld, wert, setter) => {
      const std = standardTextRef.current[feld];
      if (!std || wert !== std.text) return;
      const text = htmlToPlain(resolvePlaceholders(std.inhalt, customer, settings, docNumber, lohnanteilFuerText, docContextFuerText));
      if (text !== wert) { std.text = text; setter(text); }
    };
    nachziehen("vortext", vortext, setVortext);
    nachziehen("schlusstext", schlusstext, setSchlusstext);
  }, [customer, settings, docNumber, effectiveLohnanteil, vatRate, dueDays, depositAmount]); // eslint-disable-line react-hooks/exhaustive-deps
  // Neu angelegte AB: Vorschlag folgt dem Auftragswert, bis die Anzahlung von Hand geaendert wird
  const _vorschlagTotal = depositVorschlag ? calculateTotals().total : null;
  useEffect(() => {
    if (_vorschlagTotal != null) setDepositAmount(Math.round(_vorschlagTotal * 30) / 100);
  }, [_vorschlagTotal]);

  // karte = gewaehlte Antwortkarte (Anzahlung, 05.10.2026); ohne Karte die Standard-Vorlage
  const oeffneSenden = async (karte = null, mitPdf = true) => {
    if (isNew) { toast.error("Bitte speichern Sie zuerst das Dokument"); return; }
    if (!(await ensureLohnanteilOrConfirm())) return;
    // Still speichern: fehlende Angaben stehen als Hinweise in der Vorschau
    const savedId = await persistDocument(true);
    if (!savedId) { toast.error("Dokument konnte nicht gespeichert werden"); return; }
    try {
      const res = await api.post(`/email/send-preview/${type}/${savedId}`, karte ? { vorlage_id: karte.id } : {});
      const d = res.data;
      setSendVorschau({ ...d, docId: savedId, karte, mitPdf, text: resolvePlaceholders(d.text_vorlage, customer, settings, d.nummer_vorlaeufig ? d.nummer : docNumber, lohnanteilFuerText, docContextFuerText) });
      setSendBestaetigen(false);
      setWeitereWege(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Vorschau konnte nicht geladen werden");
    }
  };
  const onSenden = () => oeffneSenden(null);

  const onWirklichSenden = async () => {
    const v = sendVorschau;
    if (!v) return;
    // A-024: Testkunde → einmalige Rückfrage, bevor irgendetwas festgeschrieben oder gesendet wird
    if (testBeleg && !window.confirm("Das ist ein Testkunde – wirklich senden?\n\nOK = senden, Abbrechen = nicht senden")) return;
    if (!(await festschreibenFallsAngebot(v.docId))) return;
    setSending(true);
    try {
      const res = await mitTestkundeRueckfrage((extra) => api.post(`/email/send/${type}/${v.docId}`, { to: v.to.trim(), cc: v.cc || [], subject: v.subject.trim(), text: v.text, mit_pdf: v.mitPdf !== false, ...(v.nummer_vorlaeufig && { vorlaeufige_nummer: v.nummer }), ...(testBeleg && { testkunde_ok: true }), ...extra }));
      toast.success(`Gesendet an ${res.data.sent_to}`);
      if (type === "invoice" && res.data.nummer) { setDocNumber(res.data.nummer); api.get(`/invoices/${v.docId}`).then((r) => r.data?.created_at && setCreatedAt(r.data.created_at)).catch(() => {}); }  // A-023: Nummer und Rechnungsdatum wurden beim Senden vergeben
      if (res.data.status) setStatus(res.data.status);
      if (type === "order" && v.karte) {
        // Zahlungsvermerk an der AB (sichtbar auch in der Projekte-Werkbank)
        await api.post(`/orders/${v.docId}/zahlungsvermerk`, { art: "karte", text: `Antwortkarte „${v.karte.title}" gesendet an ${res.data.sent_to}${v.mitPdf === false ? " (ohne PDF)" : " (mit AB-PDF)"}` }).catch(() => {});
        setAnzahlungReload(n => n + 1);
      }
      setSendVorschau(null);
      setSendBestaetigen(false);
    } catch (err) {
      if (!err?.abgebrochen) toast.error(typeof err?.response?.data?.detail === "string" ? err.response.data.detail : "E-Mail konnte nicht gesendet werden");
    } finally {
      setSending(false);
    }
  };

  const executeMailClient = async (withText, saveFirst) => {
    if (testBeleg && !window.confirm("Das ist ein Testkunde – wirklich senden?\n\nOK = weiter, Abbrechen = nicht senden")) return;  // A-024
    if (!(await ensureLohnanteilOrConfirm())) return;
    // Immer speichern, damit das angehaengte PDF den aktuellen Stand zeigt
    const savedId = await persistDocument();
    if (!savedId) return;
    if (!(await festschreibenFallsAngebot(savedId))) return;
    try {
      const ep = type === "quote" ? "quote" : type === "order" ? "order" : "invoice";
      const res = await api.get(`/eml/${ep}/${savedId}?text=${withText ? 1 : 0}&t=${Date.now()}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "message/rfc822" }));
      const a = document.createElement("a");
      a.href = url;
      const sanitize = (s) => (s || "").trim().replace(/[\\/:*?"<>|]/g, "").replace(/\s+/g, "_");
      const kundeTeil = sanitize(customer?.firma || `${customer?.vorname || ""} ${customer?.nachname || ""}`.trim());
      const betreffTeil = sanitize(betreff);
      const emlTeile = [titles[type], kundeTeil, betreffTeil, docNumber || savedId].filter(Boolean);
      a.download = `${emlTeile.join("_")}.eml`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      if (type === "invoice" && !docNumber) api.get(`/invoices/${savedId}`).then((r) => { if (r.data?.invoice_number) { setDocNumber(r.data.invoice_number); setCreatedAt(r.data.created_at); } }).catch(() => {});  // A-023: Mailprogramm-Weg = Versand = festgeschrieben
      toast.success("E-Mail mit PDF-Anhang erstellt - jetzt im Mailprogramm oeffnen");
      // Rechnung: Entwurf -> Offen (Ralph 05.10.2026), sonst wie bisher "Gesendet"
      const newStatus = type === "invoice" ? (status === "Entwurf" ? "Offen" : null)
        : (status && !["Versendet", "Gesendet", "Bezahlt", "Teilbezahlt"].includes(status) ? "Gesendet" : null);
      if (newStatus) {
        const endpoint = type === "quote" ? "quotes" : type === "order" ? "orders" : "invoices";
        api.put(`/${endpoint}/${savedId}/status`, { status: newStatus }).then(() => setStatus(newStatus)).catch(() => {});
      }
    } catch {
      toast.error("Fehler beim Erstellen der E-Mail-Datei");
    }
  };

  const { subtotal, discountAmt, netAfterDiscount, vat, total, finalAmount } = calculateTotals();
  const titelGroups = hasTitels ? getTitelGroups() : [];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  // A-015b: Projekt-Schild: ab 1536 px in der festen Kopfleiste neben der Überschrift, sonst vorn in der festen
  // Knopfleiste direkt darunter (die Kopfleiste ist mit Werkzeugen/Vorschau/… zu voll), am Handy als eigene Zeile.
  const projektSchild = (testId) => (
    <BelegProjektSchild kundeId={selectedCustomerId} value={projektId} daten={projektDaten} neu={isNew} fest={projektFest}
      onChange={(pid) => { setProjektId(pid); setProjektFest(false); }} autoOffen={projektWaehlenAusUrl} testId={testId} />
  );
  const bauvorhabenZeile = settings?.beleg_bauvorhaben_zeile !== false && aktuellesProjekt
    ? [aktuellesProjekt.titel, aktuellesProjekt.adresse].filter(Boolean).join(", ") : "";

  // ==================== RENDER ====================
  return (
    <div className="min-h-screen bg-[#E7E3DA] dark:bg-[#3A3632]">
      <HelpSlideOver />
      <EditorToolbar
        type={type} isNew={isNew} titles={titles} listPaths={listPaths} docNumber={type === "invoice" && !docNumber ? "(Entwurf)" : docNumber} status={status} selectedCustomerId={selectedCustomerId}
        isRecording={isRecording} aiLoading={aiLoading} saving={saving}
        navigate={navigate} setShowSettings={setShowSettings} startRecording={startRecording} stopRecording={stopRecording}
        handleSave={handleSave} handleExit={handleExit} handleBack={handleBack} handleDownloadPDF={handleDownloadPDF} handlePrint={handlePrint}
        onOpenBausteine={async () => { if (await bausteineMenue()) return; setSidebarTab("blocks"); setShowLeistungenPanel(true); }}
        onTogglePreview={() => setShowPreview(true)}
        onOpenDocTemplates={async () => { if (await vorlagenMenue()) return; setShowLoadTemplate(true); }}
        onToggleLohnkosten={() => (window.innerWidth >= 1280 ? setShowLohnkosten(v => !v) : lohnPanelZeigen())}
        onToggleLeistungen={() => setShowLeistungenPanel(v => !v)}
        onOpenDocCheck={() => setShowDocCheck(true)}
        onOpenPdfPreview={() => setShowPdfPreview(true)}
        zoomLevel={zoomLevel}
        setZoomLevel={setZoomLevel}
        onNaechsterSchritt={handleCopyTo} copying={copying}
        projektSchild={vorlageModus ? null : projektSchild("projekt-schild-kopf")}
        vorlageModus={vorlageModus}
      />

      <div className="pt-14 lg:pt-20">
      {/* Knopfleiste (Status, Vorschau, Senden, Speichern, Beenden): bleibt beim Scrollen unter der
          Werkzeugleiste stehen (05.10.2026, Ralph). Ausserhalb der Zoom-Huelle, weil "sticky"
          in dem seitlich scrollbaren Bereich nicht greift. Handy: eine Zeile, nur Symbole (06.10.2026).
          AB/Rechnung erstellen: oben im Knopf "Nächster Schritt" (07.10.2026).
          Neues Dokument (08.10.2026): Leiste mit "Speichern" und "Beenden" – vorher gab es bei einem neuen
          Dokument keinen Speichern-Knopf, nur den Umweg "Zurück" -> "Speichern und Beenden". */}
      {vorlageModus && (
        <div className="sticky top-12 lg:top-14 z-30 bg-[#E7E3DA] dark:bg-[#3A3632] px-2 py-2 lg:py-3" data-testid="vorlage-leiste">
          <div className="mx-auto" style={{ maxWidth: `${794 * (zoomLevel / 100)}px`, minWidth: "min(100%, 340px)" }}>
            <div className="flex flex-wrap items-center gap-2 bg-amber-100 border border-amber-400 text-amber-950 rounded-sm px-3 py-2">
              <div className="flex-1 min-w-[12rem] text-sm font-semibold break-words" data-testid="vorlage-hinweis">Sie bearbeiten die Vorlage „{vorlageName}“</div>
              <button type="button" onClick={vorlageSpeichern} disabled={saving} data-testid="btn-vorlage-speichern"
                className="h-9 px-3 rounded-sm text-sm font-medium text-white bg-primary hover:bg-primary/90 disabled:opacity-50 flex items-center gap-1"><Save className="w-4 h-4" />Vorlage speichern</button>
              <button type="button" onClick={() => setAlsNeuName(`${vorlageName} (Kopie)`)} disabled={saving} data-testid="btn-vorlage-als-neue"
                className="h-9 px-3 rounded-sm text-sm font-medium border border-amber-600 bg-white text-amber-950 hover:bg-amber-50 disabled:opacity-50">Diese Vorlage als neue Vorlage übernehmen</button>
              <button type="button" onClick={vorlageSchliessen} disabled={saving} data-testid="btn-vorlage-schliessen"
                className="h-9 px-3 rounded-sm text-sm font-medium text-white bg-destructive hover:bg-destructive/90 disabled:opacity-50 flex items-center gap-1"><X className="w-4 h-4" />Schließen</button>
            </div>
          </div>
        </div>
      )}
      {alsNeuName !== null && (
        <Modal isOpen={true} onClose={() => setAlsNeuName(null)} title="Als neue Vorlage übernehmen" size="sm">
          <div className="space-y-3">
            <label className="block text-sm font-medium">Name der neuen Vorlage</label>
            <input value={alsNeuName} onChange={(e) => setAlsNeuName(e.target.value)} className="w-full border rounded-sm p-2 text-sm" autoFocus data-keine-warnung data-testid="vorlage-neuer-name" />
            <p className="text-xs text-muted-foreground">Die Vorlage „{vorlageName}“ bleibt unverändert.</p>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setAlsNeuName(null)} className="px-4 py-2 text-sm border rounded-sm hover:bg-muted">Abbrechen</button>
              <button type="button" onClick={vorlageAlsNeue} disabled={saving} className="px-4 py-2 text-sm bg-primary text-primary-foreground rounded-sm hover:bg-primary/90 disabled:opacity-50" data-testid="vorlage-neuer-name-ok">Neue Vorlage anlegen</button>
            </div>
          </div>
        </Modal>
      )}
      {!vorlageModus && (
        <div className="sticky top-12 lg:top-14 z-30 bg-[#E7E3DA] dark:bg-[#3A3632] px-2 py-2 lg:py-3" data-testid="editor-knopfleiste">
          <div className="mx-auto" style={{ maxWidth: `${794 * (zoomLevel / 100)}px` }}>
            <div className="flex flex-nowrap sm:flex-wrap items-center justify-between gap-1.5 sm:gap-2">
              <div className="flex flex-nowrap sm:flex-wrap items-center gap-1.5 sm:gap-2 min-w-0">
                {isNew ? <span className="text-xs lg:text-sm text-muted-foreground" data-testid="hinweis-neues-dokument">Neu – noch nicht gespeichert</span> : (
                <select value={status} onChange={(e) => setStatus(e.target.value)} title="Status"
                  className="h-8 lg:h-9 min-w-0 max-w-[7.5rem] sm:max-w-none rounded-sm border border-border bg-background text-foreground px-1.5 sm:px-2 lg:px-3 text-xs lg:text-sm">
                  {type === "quote" && (<><option value="Entwurf">Status: Entwurf</option><option value="Gesendet">Status: Gesendet</option><option value="Beauftragt">Status: Beauftragt</option><option value="Abgelehnt">Status: Abgelehnt</option></>)}
                  {type === "order" && (<><option value="Offen">Status: Offen</option><option value="In Arbeit">Status: In Arbeit</option><option value="Gesendet">Status: Gesendet</option><option value="Abgeschlossen">Status: Abgeschlossen</option></>)}
                  {type === "invoice" && (<><option value="Entwurf">Status: Entwurf</option><option value="Offen">Status: Offen</option><option value="Gesendet">Status: Gesendet</option><option value="Bezahlt">Status: Bezahlt</option><option value="Überfällig">Status: Überfällig</option></>)}
                </select>
                )}
                {/* A-015b: Projekt-Schild in der festen Knopfleiste (bis 1536 px; darüber in der Kopfleiste neben der Überschrift) */}
                <div className="hidden sm:block 2xl:hidden min-w-0">{projektSchild("projekt-schild")}</div>
              </div>
              <div className="flex flex-nowrap sm:flex-wrap items-center gap-1.5 sm:gap-2 shrink-0">
                {!isNew && (<>
                <button type="button" onClick={() => setShowPdfPreview(true)} data-testid="btn-vorschau-document-row2" title="PDF-Vorschau (so wird gedruckt)" aria-label="Vorschau"
                  className="h-8 lg:h-9 px-2 sm:px-3 rounded-sm text-xs lg:text-sm font-medium border border-border bg-background text-foreground hover:bg-muted flex items-center gap-1">
                  <FileSearch className="w-4 h-4" /><span className="hidden sm:inline">Vorschau</span>
                </button>
                <button type="button" onClick={onSenden} disabled={saving || sending || uebungsbeleg} data-testid="btn-senden-document-row2" title={uebungsbeleg ? "Ein Übungsbeleg wird nicht verschickt" : "Senden"} aria-label="Senden"
                  className="h-8 lg:h-9 px-2 sm:px-4 rounded-sm text-xs lg:text-sm font-semibold text-white transition-colors disabled:opacity-50 bg-green-700 hover:bg-green-800 flex items-center gap-1">
                  <Send className="w-4 h-4" /><span className="hidden sm:inline">Senden</span>
                </button>
                </>)}
                <button type="button" onClick={handleSave} disabled={saving} data-testid="btn-save-document-row2" title="Speichern" aria-label="Speichern"
                  className="h-8 lg:h-9 px-2 sm:px-3 rounded-sm text-xs lg:text-sm font-medium text-white transition-colors disabled:opacity-50 bg-primary hover:bg-primary/90 flex items-center gap-1">
                  <Save className="w-4 h-4" /><span className="hidden sm:inline">{saving ? "..." : "Speichern"}</span>
                </button>
                <button type="button" onClick={handleExit} disabled={saving} data-testid="btn-exit-document-row2" title="Beenden" aria-label="Beenden"
                  className="h-8 lg:h-9 px-2 sm:px-3 rounded-sm text-xs lg:text-sm font-medium text-white transition-colors disabled:opacity-50 bg-destructive hover:bg-destructive/90 flex items-center gap-1">
                  <X className="w-4 h-4" /><span className="hidden sm:inline">Beenden</span>
                </button>
              </div>
            </div>
            {/* A-015b: Handy – Projekt-Schild als eigene Zeile, bleibt mit der Knopfleiste stehen */}
            <div className="sm:hidden mt-1.5">{projektSchild("projekt-schild-handy")}</div>
          </div>
        </div>
      )}
      <div className="pb-4 lg:pb-8 overflow-x-auto overflow-y-visible">
        <div
          className="mx-auto"
          style={{
            width: `${794 * (zoomLevel / 100)}px`,
          }}
        >

          <div style={{ width: '794px', transform: `scale(${zoomLevel / 100})`, transformOrigin: 'top left' }}>
            {/* Paper Document */}
            <div className="bg-white papier text-slate-800 shadow-xl rounded-sm border [&_input]:bg-white [&_input]:text-slate-800 [&_textarea]:bg-white [&_textarea]:text-slate-800" style={{ minHeight: "600px" }}
              onDragOver={handleDragOver} onDrop={handleDrop} data-testid="document-drop-zone">

              <DocumentHeader
                settings={settings} customer={customer} customers={customers}
                selectedCustomerId={selectedCustomerId} handleCustomerChange={handleCustomerChange}
                onAnschrift={async (zeilen) => {  // A-020: Anschrift nur für diesen Beleg (Ausnahme); null = Standard aus Kundendaten
                  if (zeilen === null) {
                    try { const r = await api.get(`/modules/kunden/data/${customer?.id || selectedCustomerId}`); setCustomer(kundeFuerBeleg(r.data)); setNeueAnschrift(null); }
                    catch { toast.error("Kundendaten konnten nicht geladen werden"); }
                    return;
                  }
                  setCustomer((c) => ({ ...c, name: zeilen[0], address: zeilen.slice(1).join("\n"), anschrift_frei: true, anschrift_hand: true }));
                }}
                neueAnschrift={neueAnschrift}
                onNeueAnschrift={() => { setCustomer(neueAnschrift); setNeueAnschrift(null); }}
                onKundeAngelegt={(k) => {  // Ralph 22.10.: Kunde im Beleg angelegt → in die Liste und gleich übernehmen
                  setCustomers((alt) => (alt.some((c) => c.id === k.id) ? alt : [...alt, k]));
                  handleCustomerChange(k.id, k);
                }}
                type={type} docNumber={docNumber} abNummer={abNummer} createdAt={createdAt}
                setPositions={setPositions} positions={positions}
              />

              {/* Dokument-Ueberschrift: grosse Titel-Zeile ueber Betreff (wie altes System) */}
              <div className="px-4 lg:px-10 py-4 lg:py-5 border-b bg-gradient-to-b from-primary/5 to-transparent">
                <div className="flex items-baseline gap-4 flex-wrap">
                  <h2 className="text-2xl lg:text-3xl font-bold" style={{ color: "#003399" }}>
                    {titles[type]}
                  </h2>
                  <span className={`text-xl lg:text-2xl font-semibold font-mono ${type === "invoice" && !docNumber ? "text-amber-700" : "text-slate-700"}`} data-testid="beleg-nummer-anzeige">
                    {vorlageModus ? "Vorlage" : docNumber || (type === "invoice" ? (testBeleg ? "(Übungsnummer)" : "Entwurf") : isNew ? "(wird beim Speichern vergeben)" : "")}
                  </span>
                  {testBeleg && <TestMarke titel="Testkunde – dieser Beleg ist Übung: zählt nirgends mit, jederzeit löschbar" />}
                  {testBeleg && type !== "invoice" && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 font-semibold" data-testid="badge-testbeleg">TESTBELEG – nicht gültig</span>
                  )}
                  {type === "invoice" && belegInfo.art === "storno" && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700 font-medium" data-testid="badge-storno">Stornorechnung zu {belegInfo.storno_zu_nummer}</span>
                  )}
                  {type === "invoice" && belegInfo.storniert_durch_nummer && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700 font-medium" data-testid="badge-storniert">Storniert durch {belegInfo.storniert_durch_nummer}</span>
                  )}
                  {type === "invoice" && uebungsbeleg && !vorlageModus && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 font-semibold" data-testid="badge-uebungsbeleg">ÜBUNGSBELEG – keine Rechnung</span>
                  )}
                  {type === "invoice" && isNew && !testBeleg && (
                    <label className="text-sm flex items-center gap-1.5 cursor-pointer select-none" title="Zum Üben: eigene Nummer UEB-…, zählt nicht zu Umsatz, Mahnwesen und Buchhaltung, jederzeit löschbar">
                      <input type="checkbox" checked={uebungsbeleg} onChange={(e) => setUebungsbeleg(e.target.checked)} data-testid="check-uebungsbeleg" />
                      Übungsbeleg
                    </label>
                  )}
                  {rechnungOhneNummer() && !isNew && (
                    <Button variant="outline" size="sm" data-testid="btn-festschreiben" title="Rechnung festschreiben: sie bekommt jetzt ihre Nummer"
                      onClick={async () => { const sid = await persistDocument(); if (sid) await rechnungFestschreiben(sid); }}>
                      <FileCheck2 className="w-4 h-4" /> Festschreiben
                    </Button>
                  )}
                  <div className="flex-1" />
                  {createdAt && (
                    <span className="text-sm text-muted-foreground flex items-center gap-1">
                      Datum:
                      <input type="date" value={new Date(createdAt).toISOString().slice(0, 10)} onChange={(e) => { if (e.target.value) setCreatedAt(new Date(e.target.value + "T12:00:00").toISOString()); }} className="font-medium text-foreground border rounded-sm px-1.5 py-0.5 text-sm bg-background" data-testid="input-document-datum" />
                    </span>
                  )}
                </div>
              </div>

              {/* Betreff */}
              <div className="px-4 lg:px-10 py-3 lg:py-4 border-b">
                <div className="flex items-start justify-between gap-2 mb-1">
                  <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Betreff</span>
                  <TextKorrekturButton
                    text={betreff}
                    onTrigger={() => setKorrekturTarget({ kontext: "betreff", label: "Betreff", text: betreff, setter: setBetreff })}
                    testId="btn-korrektur-betreff"
                  />
                </div>
                <TextTemplateSelect docType={docTypeMap[type]} textType="betreff" value={betreff} onChange={setBetreff} customer={customer} settings={settings} docNumber={docNumber} lohnanteilData={{ netto: effectiveLohnanteil, mwst: lohnanteilMwst, brutto: lohnanteilBrutto, vatRate }} docContext={{ due_days: dueDays != null ? Number(dueDays) : undefined, anzahlung: depositAmount }} />
                {/* A-015b: Bauvorhaben (wie im PDF); abschaltbar in Einstellungen → Dokumente */}
                {bauvorhabenZeile && (
                  <div className="mt-2 text-sm text-slate-800" data-testid="beleg-bauvorhaben">
                    <span className="font-semibold">Bauvorhaben:</span> {bauvorhabenZeile}
                    <BauvorhabenStift projekt={aktuellesProjekt} onGespeichert={() => setProjektNeu((n) => n + 1)} />
                  </div>
                )}
              </div>

              {/* Vortext */}
              <div className="px-4 lg:px-10 py-3 lg:py-4 border-b">
                {texteVorher && nochNichtVerschickt(type, status) && (htmlToPlain(texteVorher.vortext) !== vortext || htmlToPlain(texteVorher.schlusstext) !== schlusstext) && (
                  <div className="mb-2 flex flex-wrap items-center gap-2 text-xs bg-emerald-50 border border-emerald-200 text-emerald-900 rounded-sm px-3 py-2" data-testid="standardtexte-hinweis">
                    <span className="flex-1 min-w-[12rem]">Standard-Vortext und -Schlusstext der {titles[type]} sind eingesetzt.</span>
                    <button type="button" data-testid="btn-text-aus-quelle"
                      onClick={() => { setVortext(htmlToPlain(texteVorher.vortext)); setSchlusstext(htmlToPlain(texteVorher.schlusstext)); toast.success(`Text aus ${texteVorher.von || "der Vorlage"} übernommen – bitte speichern`); }}
                      className="px-2.5 py-1 rounded-sm border border-emerald-300 bg-white hover:bg-emerald-100 font-medium">
                      Text aus {texteVorher.von || "der Vorlage"} übernehmen
                    </button>
                    <button type="button" onClick={() => setTexteVorher(null)} aria-label="Hinweis schließen" className="px-1.5 text-emerald-700 hover:text-emerald-900" data-testid="btn-standardtexte-hinweis-zu">×</button>
                  </div>
                )}
                {customer && !customer.anrede && (
                  <div
                    className="mb-2 flex items-start gap-2 text-xs bg-amber-50 dark:bg-amber-100 border border-amber-200 dark:border-amber-300 text-amber-800 dark:text-amber-900 rounded-sm px-3 py-2"
                    data-testid="anrede-missing-hint"
                  >
                    <span className="text-amber-600 flex-shrink-0">⚠</span>
                    <span className="flex-1">
                      Keine Anrede beim Kunden hinterlegt – im Vortext wird <strong>„Sehr geehrte Damen und Herren"</strong> verwendet.
                    </span>
                  </div>
                )}
                {customer && customer.anrede && (() => {
                  const vorschlag = `${getAnredeBrief(customer)},`;
                  const schonDrin = (vortext || "").trim().startsWith(vorschlag);
                  if (schonDrin) return null;
                  return (
                    <div
                      className="mb-2 flex items-center gap-2 text-xs bg-blue-50 dark:bg-blue-100 border border-blue-200 dark:border-blue-300 text-blue-900 rounded-sm px-3 py-2"
                      data-testid="anrede-vorschlag-box"
                    >
                      <span className="text-blue-600 flex-shrink-0">💡</span>
                      <span className="flex-1">
                        Vorgeschlagene Anrede: <strong>„{vorschlag}"</strong>
                      </span>
                      <button
                        type="button"
                        onClick={() => {
                          const rest = (vortext || "").trim();
                          setVortext(rest ? `${vorschlag}\n\n${rest}` : vorschlag);
                          toast.success("Anrede uebernommen");
                        }}
                        data-testid="btn-anrede-uebernehmen"
                        className="px-2.5 py-1 rounded-sm bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium transition-colors flex-shrink-0"
                      >
                        Übernehmen
                      </button>
                    </div>
                  );
                })()}
                <div className="flex items-start justify-between gap-2 mb-1">
                  <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Vortext</span>
                  <TextKorrekturButton
                    text={vortext}
                    onTrigger={() => setKorrekturTarget({ kontext: "vortext", label: "Vortext", text: vortext, setter: setVortext })}
                    testId="btn-korrektur-vortext"
                  />
                </div>
                <TextTemplateSelect docType={docTypeMap[type]} textType="vortext" value={vortext} onChange={(v) => setVortext(htmlToPlain(v))} customer={customer} settings={settings} docNumber={docNumber} lohnanteilData={{ netto: effectiveLohnanteil, mwst: lohnanteilMwst, brutto: lohnanteilBrutto, vatRate }} docContext={{ due_days: dueDays != null ? Number(dueDays) : undefined, anzahlung: depositAmount }} />
              </div>

              <PositionsTable
                positions={positions} numbering={numbering} type={type}
                selectedPositions={selectedPositions} togglePositionSelect={togglePositionSelect}
                updatePosition={updatePosition} removePosition={removePosition} movePosition={movePosition}
                checkStammChange={checkStammChange} stammChangeIdx={stammChangeIdx} setStammChangeIdx={setStammChangeIdx} saveToStamm={saveToStamm}
                titelTemplates={titelTemplates} titelDropdownIdx={titelDropdownIdx} setTitelDropdownIdx={setTitelDropdownIdx} saveTitelTemplate={saveTitelTemplate}
                dragIndex={dragIndex} setDragIndex={setDragIndex} dragOverIndex={dragOverIndex} setDragOverIndex={setDragOverIndex}
                saveAsArticleIdx={saveAsArticleIdx} setSaveAsArticleIdx={setSaveAsArticleIdx} saveAsType={saveAsType} setSaveAsType={setSaveAsType}
                handleSavePositionAsArticle={handleSavePositionAsArticle}
                addPosition={addPosition} addTitel={addTitel}
                blockSaveName={blockSaveName} setBlockSaveName={setBlockSaveName} saveAsLeistungsBlock={saveAsLeistungsBlock} setSelectedPositions={setSelectedPositions}
                leistungsBloecke={leistungsBloecke} ersetzeLeistungsBlock={ersetzeLeistungsBlock}
                articles={articles} services={services} addFromStamm={addFromStamm}
                onOpenKalkulation={openPositionKalk} activeKalkIdx={activeKalkIdx}
              />

              <TotalsSection
                hasTitels={hasTitels} titelGroups={titelGroups} subtotal={subtotal}
                discount={discount} setDiscount={setDiscount} discountType={discountType}
                discountAmt={discountAmt} netAfterDiscount={netAfterDiscount}
                vatRate={vatRate} setVatRate={setVatRate} vat={vat} total={total}
                type={type} depositAmount={depositAmount} setDepositAmount={(v) => { setDepositAmount(v); setDepositVorschlag(false); }} finalAmount={finalAmount}
                depositLocked={type === "invoice" && anzahlungsAbzuege.length > 0}
                dueDays={dueDays} setDueDays={setDueDays} createdAt={createdAt}
                showLohnanteil={showLohnanteil} setShowLohnanteil={setShowLohnanteil}
                effectiveLohnanteil={effectiveLohnanteil} lohnanteilMwst={lohnanteilMwst} lohnanteilBrutto={lohnanteilBrutto}
                lohnanteilCustom={lohnanteilCustom} setLohnanteilCustom={setLohnanteilCustom} totalLaborCost={totalLaborCost}
              />

              {type === "order" && !isNew && (
                <AnzahlungPanel orderId={id}
                  depositAmount={depositAmount} vorschlag={depositVorschlag} reloadKey={anzahlungReload}
                  onStand={setAnzahlungStand}
                  onKarteSenden={(karte, mitPdf) => oeffneSenden(karte, mitPdf)} navigate={navigate}
                  vorSpeichern={() => persistDocument(true)} />
              )}
              {type === "invoice" && anzahlungsAbzuege.length > 0 && (
                <div className="px-4 lg:px-10 py-2 border-t text-xs text-muted-foreground" data-testid="schluss-abzuege">
                  {anzahlungsAbzuege.map(a => (
                    <div key={a.invoice_id}>Abzgl. Anzahlungsrechnung {a.nummer} vom {new Date(a.datum).toLocaleDateString("de-DE")}: Netto {Number(a.netto).toFixed(2)} € + MwSt {Number(a.mwst).toFixed(2)} € = {Number(a.brutto).toFixed(2)} €</div>
                  ))}
                </div>
              )}

              {type === "order" && (
                <div className="px-4 lg:px-10 py-3 lg:py-4 border-t">
                  <label htmlFor="deposit-note" className="block text-xs font-semibold text-muted-foreground mb-2">
                    Zusätzlicher Hinweis zur Anzahlung (optional)
                  </label>
                  <textarea
                    id="deposit-note"
                    data-testid="deposit-note"
                    value={depositNote}
                    onChange={(e) => setDepositNote(e.target.value)}
                    maxLength={2000}
                    rows={3}
                    placeholder="Hier können Sie einen individuellen Hinweis zur vereinbarten Anzahlung ergänzen."
                    className="w-full rounded-md border border-input bg-background p-3 text-sm"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Wird bei vereinbarter Anzahlung unter dem Anzahlungsbetrag in der AB gedruckt.
                  </p>
                </div>
              )}

              {/* Schlusstext */}
              <div className="px-4 lg:px-10 py-3 lg:py-4 border-t">
                <div className="flex items-start justify-between gap-2 mb-1">
                  <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Schlusstext</span>
                  <TextKorrekturButton
                    text={schlusstext}
                    onTrigger={() => setKorrekturTarget({ kontext: "schlusstext", label: "Schlusstext", text: schlusstext, setter: setSchlusstext })}
                    testId="btn-korrektur-schlusstext"
                  />
                </div>
                <TextTemplateSelect docType={docTypeMap[type]} textType="schlusstext" value={schlusstext} onChange={(v) => setSchlusstext(htmlToPlain(v))} customer={customer} settings={settings} docNumber={docNumber} lohnanteilData={{ netto: effectiveLohnanteil, mwst: lohnanteilMwst, brutto: lohnanteilBrutto, vatRate }} docContext={{ due_days: dueDays != null ? Number(dueDays) : undefined, anzahlung: depositAmount }} />
              </div>

              {/* Footer (3-spaltig wie PDF: Firma+Inhaber+Adresse · Kontakt+St.-Nr. · Bank) */}
              <div className="px-4 lg:px-10 py-3 lg:py-4 border-t text-[9px] lg:text-[10px] text-slate-600" data-testid="document-footer">
                <div className="border-t border-slate-200 pt-2 grid grid-cols-3 gap-4 leading-tight">
                  <div>
                    <p className="font-bold text-slate-700">{settings.company_name || "Tischlerei Graupner"}</p>
                    {settings.owner_name && <p>Inh. {settings.owner_name}</p>}
                    {(settings.address || "").split("\n").slice(0, 2).map((line, i) => (
                      <p key={i}>{line.trim()}</p>
                    ))}
                  </div>
                  <div>
                    {settings.phone && <p>Tel: {settings.phone}</p>}
                    {settings.email && <p>{settings.email}</p>}
                    {settings.website && <p>{settings.website}</p>}
                    {settings.tax_id && <p>St.-Nr.: {settings.tax_id}</p>}
                  </div>
                  <div>
                    {settings.bank_name && <p>{settings.bank_name}</p>}
                    {settings.iban && <p>IBAN: {settings.iban}</p>}
                    {settings.bic && <p>BIC: {settings.bic}</p>}
                  </div>
                </div>
                <div className="text-right text-[8px] text-slate-400 mt-1">Seite 1</div>
              </div>
            </div>
          </div>

        </div>
      </div>
      {/* BUT-0030: schmale Fenster/Handy – Lohnkosten unter dem Beleg in Bildschirmbreite (nicht im breiten Papier) */}
      <LohnkostenSidebar
        unten zeilen={lohnZeilenListe} summe={totalLaborCost} vatRate={vatRate}
        showLohnanteil={showLohnanteil} setShowLohnanteil={setShowLohnanteil}
        lohnanteilCustom={lohnanteilCustom} setLohnanteilCustom={setLohnanteilCustom}
        onHand={lohnHandSetzen} onAutomatik={lohnAutomatik}
      />
      </div>

      {/* Leistungen & Artikel Slide-Over, ueber Werkzeugleiste erreichbar, unabhaengig vom Scrollen */}
      {showLeistungenPanel && (
        <div className={`fixed left-0 bottom-0 z-40 bg-card border-r shadow-2xl overflow-hidden flex flex-col ${handyAnsicht ? "inset-0 z-50 border-r-0" : "top-14 lg:top-20"}`}
          style={handyAnsicht ? undefined : { width: leisteBreiteGueltig() }} data-testid="leistungen-panel">
          <div className="flex items-center justify-between px-4 py-3 border-b shrink-0">
            {handyAnsicht ? (
              <button onClick={() => setShowLeistungenPanel(false)} className="text-sm font-semibold text-primary" data-testid="btn-close-leistungen" title="Zurück zum Beleg">← {titles[type]}</button>
            ) : null}
            <h2 className="text-base font-semibold">{sidebarTab === "blocks" ? "Bausteine" : "Leistungen, Artikel & Bausteine"}</h2>
            {!handyAnsicht && (
              <button onClick={() => setShowLeistungenPanel(false)} className="p-1 hover:bg-muted rounded-sm" data-testid="btn-close-leistungen" title="Schliessen">
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          <div className="p-4 flex-1 min-h-0 overflow-y-auto">
            <EditorSidebar
              sidebarSearch={sidebarSearch} setSidebarSearch={setSidebarSearch}
              sidebarTab={sidebarTab} setSidebarTab={setSidebarTab}
              filteredServices={filteredServices} filteredArticles={filteredArticles}
              leistungsBloecke={leistungsBloecke} setLeistungsBloecke={setLeistungsBloecke}
              selectedItem={selectedItem} setSelectedItem={setSelectedItem}
              addFromStamm={addFromStamm}
              insertLeistungsBlock={(b, liste) => { const ok = insertLeistungsBlock(b, liste); if (handyAnsicht) setShowLeistungenPanel(false); return ok; }}
              onEigenesFenster={() => { if (bausteineFensterOeffnen()) { toast.success("Bausteine-Fenster geöffnet"); setShowLeistungenPanel(false); } }}
              bausteineHandy={handyAnsicht}
              handleDragStart={handleDragStart} navigate={navigate}
              settings={settings} onApplyKalkPrice={handleApplyKalkPrice}
              onItemUpdated={loadData}
            />
          </div>
          {!handyAnsicht && (
            <div onPointerDown={leisteZiehen} data-testid="leiste-ziehgriff" title="Breite ziehen"
              className="absolute top-0 right-0 bottom-0 w-2 cursor-col-resize bg-border/60 hover:bg-primary/50 touch-none" />
          )}
        </div>
      )}
      {/* Lohnkosten Slide-Over (nicht-modal, Editor bleibt klickbar) */}
      {showLohnkosten && (
        <div className="fixed right-0 top-14 lg:top-20 bottom-0 w-full sm:w-[360px] z-40 bg-card border-l shadow-2xl overflow-y-auto" data-testid="lohnkosten-panel">
          <div className="flex items-center justify-between px-4 py-3 border-b">
            <h2 className="text-base font-semibold">Lohnkosten</h2>
            <button onClick={() => setShowLohnkosten(false)} className="p-1 hover:bg-muted rounded-sm" data-testid="btn-close-lohnkosten" title="Schliessen">
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="p-4">
            <LohnkostenSidebar
              zeilen={lohnZeilenListe} summe={totalLaborCost} vatRate={vatRate}
              showLohnanteil={showLohnanteil}
              setShowLohnanteil={setShowLohnanteil}
              lohnanteilCustom={lohnanteilCustom}
              setLohnanteilCustom={setLohnanteilCustom}
              onHand={lohnHandSetzen} onAutomatik={lohnAutomatik}
            />
          </div>
        </div>
      )}

      {/* In-App-E-Mail (SMTP) entfernt – Versand nur noch über Mailprogramm (Betterbird/.eml) */}

      <SettingsSlideOver
        showSettings={showSettings} setShowSettings={setShowSettings}
        settingsTab={settingsTab} setSettingsTab={setSettingsTab}
        articles={articles} services={services} leistungsBloecke={leistungsBloecke}
        insertLeistungsBlock={insertLeistungsBlock}
        loadData={loadData} setLeistungsBloecke={setLeistungsBloecke}
      />

      {showPreview && (
        <DocumentPreview
          isOpen={true}
          type={type}
          document={getPreviewDocument()}
          onClose={() => setShowPreview(false)}
          onEdit={() => setShowPreview(false)}
          onFestgeschrieben={(q) => { setCreatedAt(q.created_at); setValidUntil(q.valid_until || null); setVersendetAm(q.versendet_am || null); }}
        />
      )}

      {/* Senden: Mail-Vorschau (Klick 1) und "Wirklich senden?" (Klick 2) – 05.10.2026 */}
      {sendVorschau && !sendBestaetigen && (
        <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4" data-testid="senden-vorschau-dialog">
          <div className="bg-background rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">
            <div className="p-5 border-b">
              <h3 className="text-lg font-semibold">{sendVorschau.label} {sendVorschau.nummer} senden</h3>
              <p className="text-sm text-muted-foreground mt-1">So kommt die E-Mail beim Kunden an. {sendVorschau.mitPdf === false ? "Ohne PDF-Anhang." : "Das PDF wird angehängt."}</p>
            </div>
            <div className="p-5 space-y-3 overflow-y-auto flex-1">
              {sendVorschau.hinweise?.length > 0 && (
                <div className="p-3 rounded-sm bg-amber-50 border border-amber-200 text-sm text-amber-900" data-testid="senden-hinweise">
                  <div className="font-semibold mb-1">Bitte prüfen:</div>
                  <ul className="list-disc pl-5 space-y-0.5">{sendVorschau.hinweise.map((h, i) => <li key={i}>{h}</li>)}</ul>
                </div>
              )}
              <div>
                <label className="text-xs font-semibold text-muted-foreground uppercase mb-1 block">An</label>
                <input value={sendVorschau.to} onChange={(e) => setSendVorschau(v => ({ ...v, to: e.target.value }))}
                  className="w-full h-9 rounded-sm border border-input bg-background px-3 text-sm" data-testid="input-senden-an" />
                {(sendVorschau.adressen || []).length > 1 && (
                  <select className="mt-1 w-full h-9 rounded-sm border border-input bg-background px-2 text-sm" data-testid="senden-an-auswahl"
                    value={(sendVorschau.adressen || []).some((a) => a.adresse === sendVorschau.to.trim().toLowerCase()) ? sendVorschau.to.trim().toLowerCase() : ""}
                    onChange={(e) => e.target.value && setSendVorschau(v => ({ ...v, to: e.target.value, cc: (v.cc || []).filter((c) => c !== e.target.value) }))}>
                    {!(sendVorschau.adressen || []).some((a) => a.adresse === sendVorschau.to.trim().toLowerCase()) && <option value="">– andere Adresse (oben eingetragen) –</option>}
                    {sendVorschau.adressen.map((a) => <option key={a.adresse} value={a.adresse}>{a.adresse}{a.gefunden_ueber ? ` (${a.gefunden_ueber})` : ""}</option>)}
                  </select>
                )}
                {sendVorschau.to.trim().toLowerCase() !== (sendVorschau.kunden_email || "").toLowerCase() && (
                  <p className="text-xs text-amber-700 mt-1" data-testid="senden-an-abweichend">
                    {sendVorschau.kunden_email ? `Abweichend von der Kundenadresse ${sendVorschau.kunden_email}` : "Beim Kunden ist keine Adresse hinterlegt"}
                  </p>
                )}
              </div>
              <div data-testid="senden-cc-bereich">
                <label className="text-xs font-semibold text-muted-foreground uppercase mb-1 block">Kopie (CC)</label>
                <div className="flex flex-wrap items-center gap-1.5">
                  {(sendVorschau.cc || []).length === 0 && <span className="text-xs text-muted-foreground">keine Kopie</span>}
                  {(sendVorschau.cc || []).map((c, i) => (
                    <span key={c} className="inline-flex items-center gap-1 rounded-full border bg-muted/40 pl-2 pr-1 py-0.5 text-xs max-w-full" data-testid={`senden-cc-chip-${i}`}>
                      <span className="break-all">{c}</span>
                      <button type="button" onClick={() => setSendVorschau(v => ({ ...v, cc: v.cc.filter((x) => x !== c) }))} className="px-1 rounded hover:bg-muted" title="Kopie entfernen" data-testid={`senden-cc-entfernen-${i}`}>×</button>
                    </span>
                  ))}
                </div>
                <div className="flex flex-wrap gap-1.5 mt-1">
                  {(sendVorschau.adressen || []).filter((a) => a.adresse !== sendVorschau.to.trim().toLowerCase() && !(sendVorschau.cc || []).includes(a.adresse)).length > 0 && (
                    <select className="h-8 rounded-sm border border-input bg-background px-2 text-xs max-w-full" value="" data-testid="senden-cc-auswahl"
                      onChange={(e) => e.target.value && setSendVorschau(v => ({ ...v, cc: [...(v.cc || []), e.target.value] }))}>
                      <option value="">+ Kopie an … (aus den Adressen des Kunden)</option>
                      {sendVorschau.adressen.filter((a) => a.adresse !== sendVorschau.to.trim().toLowerCase() && !(sendVorschau.cc || []).includes(a.adresse))
                        .map((a) => <option key={a.adresse} value={a.adresse}>{a.adresse}{a.gefunden_ueber ? ` (${a.gefunden_ueber})` : ""}</option>)}
                    </select>
                  )}
                  <input placeholder="andere Kopie-Adresse + Enter" className="h-8 flex-1 min-w-[10rem] rounded-sm border border-input bg-background px-2 text-xs" data-testid="senden-cc-eingabe"
                    onKeyDown={(e) => {
                      if (e.key !== "Enter") return;
                      e.preventDefault();
                      const a = e.target.value.trim().toLowerCase();
                      if (/^[^\s@,;<>]+@[^\s@,;<>]+\.[^\s@,;<>]+$/.test(a) && a !== sendVorschau.to.trim().toLowerCase() && !(sendVorschau.cc || []).includes(a)) {
                        setSendVorschau(v => ({ ...v, cc: [...(v.cc || []), a] })); e.target.value = "";
                      } else toast.error("Bitte eine gültige, noch nicht eingetragene Adresse eingeben");
                    }} />
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold text-muted-foreground uppercase mb-1 block">Betreff</label>
                <input value={sendVorschau.subject} onChange={(e) => setSendVorschau(v => ({ ...v, subject: e.target.value }))}
                  className="w-full h-9 rounded-sm border border-input bg-background px-3 text-sm" data-testid="input-senden-betreff" />
              </div>
              <TextTemplateSelect docType={docTypeMap[type]} textType="email" value={sendVorschau.text}
                onChange={(v) => setSendVorschau(sv => ({ ...sv, text: htmlToPlain(v) }))}
                customer={customer} settings={settings} docNumber={docNumber} lohnanteilData={lohnanteilFuerText} docContext={docContextFuerText} />
              <div>
                <div className="text-xs font-semibold text-muted-foreground uppercase mb-1">Anhang</div>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={sendVorschau.mitPdf !== false} onChange={(e) => setSendVorschau(v => ({ ...v, mitPdf: e.target.checked }))} data-testid="chk-senden-mit-pdf" />
                  <span className={`font-mono ${sendVorschau.mitPdf === false ? "line-through text-muted-foreground" : ""}`}>{sendVorschau.filename}</span>
                </label>
              </div>
              <div className="pt-2 border-t">
                <button type="button" onClick={() => setWeitereWege(w => !w)} className="text-xs text-muted-foreground hover:underline" data-testid="btn-weitere-wege">
                  {weitereWege ? "▾" : "▸"} Weitere Wege
                </button>
                {weitereWege && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    <button type="button" onClick={() => { setSendVorschau(null); executeMailClient(true); }}
                      className="px-3 py-1.5 text-xs border rounded-sm hover:bg-muted" data-testid="btn-eml-mit-text">.eml herunterladen (mit Vor- und Schlusstext)</button>
                    <button type="button" onClick={() => { setSendVorschau(null); executeMailClient(false); }}
                      className="px-3 py-1.5 text-xs border rounded-sm hover:bg-muted" data-testid="btn-eml-ohne-text">.eml herunterladen (ohne Text)</button>
                  </div>
                )}
              </div>
            </div>
            <div className="p-4 border-t flex justify-end gap-2">
              <button onClick={() => setSendVorschau(null)} className="px-4 py-2 text-sm border rounded-sm hover:bg-muted" data-testid="btn-senden-abbrechen">Abbrechen</button>
              <button onClick={() => setSendBestaetigen(true)} disabled={!sendVorschau.to.trim() || !sendVorschau.subject.trim()}
                className="px-5 py-2 text-sm font-semibold rounded-sm bg-green-700 text-white hover:bg-green-800 disabled:opacity-50 flex items-center gap-1" data-testid="btn-senden-weiter">
                <Send className="w-4 h-4" /> Senden
              </button>
            </div>
          </div>
        </div>
      )}

      {sendVorschau && sendBestaetigen && (() => {
        const abweichend = sendVorschau.to.trim().toLowerCase() !== (sendVorschau.kunden_email || "").toLowerCase();
        return (
          <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4" data-testid="senden-bestaetigen-dialog">
            <div className="bg-background rounded-lg shadow-xl w-full max-w-md">
              <div className="p-5 border-b">
                <h3 className="text-lg font-semibold">Wirklich senden?</h3>
              </div>
              <div className="p-5 space-y-2 text-sm">
                <div className="flex justify-between gap-4"><span className="text-muted-foreground">Dokument</span><span className="font-medium">{sendVorschau.label} {sendVorschau.nummer}</span></div>
                <div className="flex justify-between gap-4"><span className="text-muted-foreground">Kunde</span><span className="font-medium text-right">{sendVorschau.kunde}</span></div>
                <div className="flex justify-between gap-4"><span className="text-muted-foreground">Betrag</span><span className="font-medium">{Number(sendVorschau.betrag || 0).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €</span></div>
                <div className="flex justify-between gap-4"><span className="text-muted-foreground">PDF-Anhang</span><span className="font-medium" data-testid="senden-anhang">{sendVorschau.mitPdf === false ? "nein" : "ja"}</span></div>
                {sendVorschau.karte && <div className="flex justify-between gap-4"><span className="text-muted-foreground">Text</span><span className="font-medium text-right">{sendVorschau.karte.title}</span></div>}
                {(sendVorschau.cc || []).length > 0 && (
                  <div className="flex justify-between gap-4" data-testid="senden-bestaetigen-cc"><span className="text-muted-foreground">Kopie</span><span className="font-medium break-all text-right">{sendVorschau.cc.join(", ")}</span></div>
                )}
                <div className={abweichend ? "p-2 -mx-2 rounded-sm bg-amber-100 border border-amber-400" : ""} data-testid="senden-empfaenger">
                  <div className="flex justify-between gap-4"><span className="text-muted-foreground">Empfänger</span><span className="font-semibold break-all text-right">{sendVorschau.to.trim()}</span></div>
                  {abweichend && (
                    <div className="text-xs text-amber-900 mt-1" data-testid="senden-empfaenger-geaendert">
                      ⚠ Geänderte Adresse – {sendVorschau.kunden_email ? `beim Kunden hinterlegt ist ${sendVorschau.kunden_email}` : "beim Kunden ist keine Adresse hinterlegt"}
                    </div>
                  )}
                </div>
              </div>
              <div className="p-4 border-t flex justify-end gap-2">
                <button onClick={() => setSendBestaetigen(false)} disabled={sending} className="px-4 py-2 text-sm border rounded-sm hover:bg-muted" data-testid="btn-senden-zurueck">Zurück</button>
                <button onClick={onWirklichSenden} disabled={sending}
                  className="px-5 py-2 text-sm font-semibold rounded-sm bg-green-700 text-white hover:bg-green-800 disabled:opacity-50" data-testid="btn-senden-ja">
                  {sending ? "Sende..." : "Ja, jetzt senden"}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {showExitConfirm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="exit-confirm-dialog">
          <div className="bg-card rounded-lg shadow-2xl p-6 max-w-sm w-full">
            <h3 className="text-lg font-semibold mb-2">Dokument beenden</h3>
            <p className="text-sm text-muted-foreground mb-6">Moechten Sie vor dem Beenden speichern?</p>
            <div className="flex flex-col gap-2">
              <button onClick={handleExitWithSaveKunde} className="w-full px-4 py-2.5 text-sm font-medium rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 transition-colors" data-testid="btn-exit-save-kunde">
                Speichern und zum Kunden
              </button>
              <button onClick={handleExitWithSaveProjekt} className="w-full px-4 py-2.5 text-sm font-medium rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 transition-colors" data-testid="btn-exit-save-projekt">
                Speichern und zum Projekt
              </button>
              <button onClick={handleExitWithSave} className="w-full px-4 py-2.5 text-sm font-medium rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 transition-colors" data-testid="btn-exit-save">
                Speichern und Beenden
              </button>
              <button onClick={handleExitWithoutSave} className="w-full px-4 py-2.5 text-sm font-medium rounded-sm border hover:bg-destructive/10 text-destructive transition-colors" data-testid="btn-exit-no-save">
                Ohne Speichern beenden
              </button>
              <button onClick={() => setShowExitConfirm(false)} className="w-full px-4 py-2.5 text-sm font-medium rounded-sm border hover:bg-muted transition-colors" data-testid="btn-exit-cancel">
                Abbrechen
              </button>
            </div>
          </div>
        </div>
      )}
      {showTemplateDialog && (
        <SaveAsTemplateDialog
          docType={type}
          docId={showTemplateDialog}
          defaultName={betreff || `${titles[type]} vom ${new Date().toLocaleDateString("de-DE")}`}
          onClose={() => setShowTemplateDialog(false)}
        />
      )}

      {showLoadTemplate && (
        <VorlageOeffnenFenster
          docType={type}
          hatPositionen={positions.some(p => (p.description || "").trim())}
          onClose={() => setShowLoadTemplate(false)}
          onApply={uebernehmeVorlagePositionen}
          onEigenesFenster={() => { if (vorlagenFensterOeffnen()) setShowLoadTemplate(false); }}
        />
      )}
      {vorlageFrage && (
        <VorlageFrage
          wahl={vorlageFrage} docType={type} hatPositionen={positions.some(p => (p.description || "").trim())}
          onApply={(t, modus, mitTexte) => { setVorlageFrage(null); uebernehmeVorlagePositionen(t, modus, mitTexte); }}
          onAbbrechen={() => setVorlageFrage(null)}
        />
      )}
      {/* inaktiv seit 01.11.2026, Entfernen nach Ralphs Prüfung: altes Fenster LoadTemplateDialog (+ applyTemplate) wird nicht mehr aufgerufen
      {showLoadTemplate && (
        <LoadTemplateDialog preferredType={type} onClose={() => setShowLoadTemplate(false)} onSelect={applyTemplate} />
      )} */}

      {korrekturTarget && (
        <TextKorrekturModal
          isOpen={true}
          original={korrekturTarget.text}
          kontext={korrekturTarget.kontext}
          feldLabel={korrekturTarget.label}
          onAccept={(corrected) => korrekturTarget.setter(corrected)}
          onClose={() => setKorrekturTarget(null)}
        />
      )}

      {showPdfPreview && (
        <PdfPreviewModal
          isOpen={true}
          onClose={() => setShowPdfPreview(false)}
          filename={(() => {
            const sanitize = (s) => (s || "").trim().replace(/[\\/:*?"<>|]/g, "").replace(/\s+/g, "_");
            const kundeTeil = sanitize(customer?.firma || `${customer?.vorname || ""} ${customer?.nachname || ""}`.trim());
            const betreffTeil = sanitize(betreff);
            const teile = [titles[type], kundeTeil, betreffTeil, docNumber].filter(Boolean);
            return `${teile.join("_")}.pdf`;
          })()}
          vorDruckUndDownload={ensureLohnanteilOrConfirm}
          getPdfBlob={async () => {
            const savedId = await persistDocument();
            if (!savedId) return null;
            const endpoint = type === "quote" ? "quote" : type === "order" ? "order" : "invoice";
            const res = await api.get(`/pdf/${endpoint}/${savedId}?t=${Date.now()}`, { responseType: "blob" });
            return new Blob([res.data], { type: "application/pdf" });
          }}
          getDownloadBlob={type === "quote" ? festgeschriebenesAngebotPdf : type === "invoice" && rechnungOhneNummer() ? festgeschriebenesRechnungPdf : undefined}
          getPrintBlob={type === "quote" ? festgeschriebenesAngebotPdf : type === "invoice" && rechnungOhneNummer() ? festgeschriebenesRechnungPdf : undefined}
        />
      )}

      {showDocCheck && (() => {
        const docFields = [
          { id: "betreff", label: "Betreff", text: betreff, kontext: "betreff" },
          { id: "vortext", label: "Vortext", text: vortext, kontext: "vortext" },
          { id: "schlusstext", label: "Schlusstext", text: schlusstext, kontext: "schlusstext" },
          { id: "notes", label: "Bemerkung", text: notes, kontext: "allgemein" },
          ...positions.map((p, i) => ({
            id: `position-${i}`,
            label: `Position ${i + 1}`,
            text: p?.description || "",
            kontext: "allgemein",
          })).filter(f => f.text.trim()),
        ];
        const lohnanteilLeer = !(effectiveLohnanteil > 0);
        const issues = validateDocument({
          customer, betreff, positions, type,
          dueDays, anzahlungProzent: depositAmount, abschlag: discount, mwstSatz: vatRate,
          lohnanteilLeer,
        });
        const applyOne = (r) => {
          if (r.id === "betreff") setBetreff(r.corrected);
          else if (r.id === "vortext") setVortext(r.corrected);
          else if (r.id === "schlusstext") setSchlusstext(r.corrected);
          else if (r.id === "notes") setNotes(r.corrected);
          else if (r.id.startsWith("position-")) {
            const idx = parseInt(r.id.split("-")[1], 10);
            updatePosition(idx, "description", r.corrected);
          }
        };
        const createLohnanteilText = async () => {
          try {
            const res = await api.get("/modules/textvorlagen/data", { params: { doc_type: "allgemein", text_type: "bemerkung" } });
            const tpl = (res.data || []).find(t => /35a|lohnanteil/i.test(t.title || ""));
            if (!tpl) { toast.error("Keine §35a-Textvorlage gefunden"); return false; }
            const lohnanteilData = { netto: effectiveLohnanteil, mwst: lohnanteilMwst, brutto: lohnanteilBrutto, vatRate };
            const text = resolvePlaceholders(tpl.content, customer, settings, docNumber, lohnanteilData);
            setSchlusstext(prev => (prev && prev.trim() ? prev.trimEnd() + "\n\n" : "") + text);
            toast.success("Lohnanteiltext in Schlusstext eingefügt");
            return true;
          } catch {
            toast.error("Fehler beim Laden der Lohnanteil-Textvorlage");
            return false;
          }
        };
        const kontextInfo = [
          `${titles[type]}${docNumber ? " " + docNumber : ""}`,
          customer ? `Kunde: ${(customer.name || customer.firma || "").trim()}` : null,
          positions?.length ? `${positions.length} Position(en)` : null,
        ].filter(Boolean).join(", ");
        return (
          <DocumentCheckModal
            isOpen={true}
            onClose={() => setShowDocCheck(false)}
            fields={docFields}
            issues={issues}
            kontextInfo={kontextInfo}
            type={type}
            lohnanteilEingetragen={effectiveLohnanteil > 0}
            onCreateLohnanteilText={createLohnanteilText}
            onApply={applyOne}
            onApplyAll={(items) => items.forEach(applyOne)}
          />
        );
      })()}
    </div>
  );
};


// ==================== SAVE AS TEMPLATE DIALOG ====================
const SaveAsTemplateDialog = ({ docType, docId, defaultName, onClose }) => {
  const [step, setStep] = useState("ask"); // ask -> name -> done
  const [name, setName] = useState(defaultName);
  const [anonymize, setAnonymize] = useState(true);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!name.trim()) { toast.error("Name erforderlich"); return; }
    setSaving(true);
    try {
      await api.post("/document-templates/from-document", {
        doc_type: docType, source_id: docId, name: name.trim(), anonymize,
      });
      setStep("done");
      toast.success("Als Vorlage gespeichert");
    } catch (e) { toast.error(e?.response?.data?.detail || "Fehler"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="save-template-dialog">
      <div className="bg-background rounded-lg shadow-xl w-full max-w-md">
        {step === "ask" && (
          <>
            <div className="p-5 border-b">
              <div className="flex items-center gap-2 mb-1">
                <Package className="w-5 h-5 text-primary" />
                <h3 className="text-lg font-semibold">Als Vorlage speichern?</h3>
              </div>
              <p className="text-sm text-muted-foreground">Du kannst dieses Dokument als wiederverwendbare Vorlage ablegen. Beim nächsten ähnlichen Auftrag in 2 Klicks wieder da.</p>
            </div>
            <div className="p-5 flex justify-end gap-2">
              <button onClick={onClose} className="px-4 py-2 text-sm border rounded-sm hover:bg-muted" data-testid="tpl-ask-no">Nein, danke</button>
              <button onClick={() => setStep("name")} className="px-4 py-2 text-sm bg-primary text-primary-foreground rounded-sm hover:bg-primary/90" data-testid="tpl-ask-yes">
                Ja, Vorlage anlegen
              </button>
            </div>
          </>
        )}
        {step === "name" && (
          <>
            <div className="p-5 border-b">
              <h3 className="text-lg font-semibold mb-1">Vorlage benennen</h3>
              <p className="text-sm text-muted-foreground">Gib einen Namen, unter dem du die Vorlage später wiederfindest.</p>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-xs font-medium mb-1">Vorlagenname</label>
                <input value={name} onChange={e => setName(e.target.value)} className="w-full border rounded-sm p-2 text-sm" autoFocus placeholder='z.B. "Hebeschiebe Wartung Standard"' data-testid="tpl-name-input" />
              </div>
              <label className="flex items-start gap-2 p-3 border rounded-sm cursor-pointer hover:bg-muted/30">
                <input type="checkbox" checked={anonymize} onChange={e => setAnonymize(e.target.checked)} className="mt-0.5" data-testid="tpl-anonymize" />
                <div>
                  <div className="text-sm font-medium">Anschrift als Max Mustermann anlegen?</div>
                  <div className="text-xs text-muted-foreground">
                    {anonymize ? "✓ Empfohlen: Kundendaten werden durch Mustermann-Anschrift ersetzt. Die Vorlage ist damit allgemein wiederverwendbar." : "✗ Die Vorlage behält die echten Kundendaten (für persönliche Vorlagen eines Stammkunden)."}
                  </div>
                </div>
              </label>
            </div>
            <div className="p-5 border-t flex justify-end gap-2">
              <button onClick={() => setStep("ask")} className="px-4 py-2 text-sm border rounded-sm hover:bg-muted">Zurück</button>
              <button onClick={save} disabled={saving || !name.trim()} className="px-4 py-2 text-sm bg-primary text-primary-foreground rounded-sm hover:bg-primary/90 disabled:opacity-50" data-testid="tpl-save-final">
                {saving ? "Speichere..." : "Als Vorlage speichern"}
              </button>
            </div>
          </>
        )}
        {step === "done" && (
          <div className="p-8 text-center">
            <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
            <h3 className="text-lg font-semibold mb-1">Gespeichert!</h3>
            <p className="text-sm text-muted-foreground mb-4">Du findest die Vorlage unter <strong>Einstellungen → Dokument-Vorlagen</strong> oder <strong>Dokumente → Vorlagen</strong>.</p>
            <button onClick={onClose} className="px-4 py-2 text-sm bg-primary text-primary-foreground rounded-sm hover:bg-primary/90">Weiter arbeiten</button>
          </div>
        )}
      </div>
    </div>
  );
};

export { WysiwygDocumentEditor };


// ==================== LOAD TEMPLATE DIALOG ====================
const TYPE_META = {
  quote: { label: "Angebot", icon: FileText, bg: "bg-blue-50 dark:bg-blue-950/40", text: "text-blue-700 dark:text-blue-300", border: "border-blue-200 dark:border-blue-900" },
  order: { label: "Auftragsbestätigung", icon: ClipboardCheck, bg: "bg-purple-50 dark:bg-purple-950/40", text: "text-purple-700 dark:text-purple-300", border: "border-purple-200 dark:border-purple-900" },
  invoice: { label: "Rechnung", icon: Receipt, bg: "bg-green-50 dark:bg-green-950/40", text: "text-green-700 dark:text-green-300", border: "border-green-200 dark:border-green-900" },
};

// inaktiv seit 01.11.2026 (wird nicht mehr aufgerufen, Ersatz: wysiwyg/VorlageOeffnenFenster.jsx), Entfernen nach Ralphs Prüfung
// eslint-disable-next-line no-unused-vars
const LoadTemplateDialog = ({ preferredType, onClose, onSelect }) => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [onlyFavs, setOnlyFavs] = useState(false);

  useEffect(() => {
    api.get("/document-templates")
      .then(r => setList(r.data))
      .catch(() => toast.error("Fehler beim Laden"))
      .finally(() => setLoading(false));
  }, []);

  const filtered = list
    .filter(t => typeFilter === "all" || t.doc_type === typeFilter)
    .filter(t => !onlyFavs || t.favorite)
    .filter(t => {
      if (!search) return true;
      const q = search.toLowerCase();
      return (t.name || "").toLowerCase().includes(q) ||
             JSON.stringify(t.snapshot?.positions || []).toLowerCase().includes(q);
    });

  const confirmSelect = (tpl) => {
    if (tpl.doc_type !== preferredType) {
      const typeLabels = { quote: "Angebot", order: "Auftragsbestätigung", invoice: "Rechnung" };
      if (!window.confirm(`Diese Vorlage ist ein "${typeLabels[tpl.doc_type]}". Möchtest du sie trotzdem in dein ${typeLabels[preferredType]} laden?`)) return;
    }
    if (!window.confirm("Bestehende Positionen und Texte werden durch die Vorlage ersetzt. Fortfahren?")) return;
    onSelect(tpl);
  };

  const stats = {
    total: list.length,
    quote: list.filter(t => t.doc_type === "quote").length,
    order: list.filter(t => t.doc_type === "order").length,
    invoice: list.filter(t => t.doc_type === "invoice").length,
  };

  const tabs = [
    { id: "all", label: "Alle", count: stats.total },
    { id: "quote", label: "Angebote", count: stats.quote },
    { id: "order", label: "Aufträge", count: stats.order },
    { id: "invoice", label: "Rechnungen", count: stats.invoice },
  ];

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="load-template-dialog">
      <div className="bg-background rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        <div className="p-4 border-b flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold flex items-center gap-2"><Package className="w-5 h-5 text-primary" /> Vorlage öffnen</h3>
            <p className="text-xs text-muted-foreground mt-1">Angebote, Auftragsbestätigungen und Rechnungen in einem Modul – wähle eine Vorlage zum Laden.</p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-muted rounded-sm">✕</button>
        </div>

        <div className="p-4 border-b space-y-2">
          <div className="relative">
            <Search className="absolute left-3 top-2.5 w-4 h-4 text-muted-foreground" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Vorlage suchen (Name oder Position)..."
              className="w-full pl-10 pr-3 py-2 border rounded-sm text-sm"
              autoFocus
              data-testid="load-tpl-search"
            />
          </div>
          <div className="flex flex-wrap gap-1">
            {tabs.map(t => (
              <button
                key={t.id}
                onClick={() => setTypeFilter(t.id)}
                className={`px-3 py-1 rounded-sm text-xs font-medium border ${typeFilter === t.id ? "bg-primary text-primary-foreground border-primary" : "hover:bg-muted border-border"}`}
                data-testid={`load-tpl-filter-${t.id}`}
              >
                {t.label} {t.count > 0 && <span className="opacity-70">({t.count})</span>}
              </button>
            ))}
            <button
              onClick={() => setOnlyFavs(!onlyFavs)}
              className={`px-3 py-1 rounded-sm text-xs font-medium border flex items-center gap-1 ${onlyFavs ? "bg-amber-100 dark:bg-amber-900 text-amber-800 dark:text-amber-200 border-amber-300 dark:border-amber-700" : "hover:bg-muted border-border"}`}
            >
              <Star className={`w-3 h-3 ${onlyFavs ? "fill-amber-500" : ""}`} /> Favoriten
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="text-center py-8 text-muted-foreground">Lade Vorlagen...</div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Package className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <div className="font-medium mb-1">{list.length === 0 ? "Noch keine Vorlagen" : "Keine Treffer"}</div>
              <div className="text-sm">
                {list.length === 0
                  ? "Speichere ein Dokument und setze den Haken 'Als Vorlage speichern' um deine erste Vorlage zu erstellen."
                  : "Passe Suche oder Filter an."}
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              {filtered.map(tpl => {
                const meta = TYPE_META[tpl.doc_type] || TYPE_META.quote;
                const Icon = meta.icon;
                const positions = tpl.snapshot?.positions?.length || 0;
                const total = tpl.snapshot?.total_gross || 0;
                const isMatching = tpl.doc_type === preferredType;
                return (
                  <button
                    key={tpl.id}
                    onClick={() => confirmSelect(tpl)}
                    className={`w-full text-left p-3 border rounded-sm hover:shadow-md hover:border-primary transition-all flex items-center gap-3 ${isMatching ? "border-primary/20 bg-primary/5" : ""}`}
                    data-testid={`load-tpl-${tpl.id}`}
                  >
                    <div className={`p-2 rounded-sm ${meta.bg} flex-shrink-0`}>
                      <Icon className={`w-5 h-5 ${meta.text}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold">{tpl.name}</span>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded ${meta.bg} ${meta.text} border ${meta.border}`}>{meta.label}</span>
                        {tpl.favorite && <Star className="w-3.5 h-3.5 fill-amber-500 text-amber-500" />}
                        {isMatching && <span className="text-[10px] text-primary font-semibold">← passt zu {TYPE_META[preferredType].label}</span>}
                      </div>
                      <div className="text-xs text-muted-foreground flex gap-3 mt-1 flex-wrap">
                        <span>{positions} Position{positions !== 1 ? "en" : ""}</span>
                        {total > 0 && <span className="font-mono">{total.toLocaleString("de-DE", { style: "currency", currency: "EUR" })}</span>}
                        {tpl.usage_count > 0 && <span>{tpl.usage_count}× genutzt</span>}
                      </div>
                    </div>
                    <span className="text-xs text-primary font-medium flex-shrink-0">Laden →</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div className="p-4 border-t flex justify-between items-center">
          <div className="text-xs text-muted-foreground">
            Tipp: Positionen und Texte aus der Vorlage werden ins aktuelle Dokument geladen. Kunde und Dokumentnummer bleiben unverändert.
          </div>
          <button onClick={onClose} className="px-4 py-2 text-sm border rounded-sm hover:bg-muted">Schließen</button>
        </div>
      </div>
    </div>
  );
};
